// use-ability.ts — browser ability invocation and named refusals for unported casts.
// Browser lanes resolve supported actions; the retired-route chokepoint refuses all other casts. The
// handle*BtnSingleClick wrappers are pure passthroughs kept for the (very large) existing onclick surface.
//
// Every function is invoked only at user-interaction time, never at module load. Deps resolve as runtime
// globals (see globals.d.ts). All 10 public names window-attached at the bottom (matches the original).

import type { ProjectileAssignment } from '../shared/projectile-targeting';
import type {} from '../shared/instant-projectile-bridge';
import { beginActionTimingInput } from '../shared/action-timing';
import { skillRollEncounterId } from '../shared/skill-roll-browser';
import { isFt3dAoeShape, type Ft3dAoePlacementEnvelope, type Ft3dAoePlacementStageEnvelope } from '../shared/ft3d-aoe-placement-contract';
import { orbLegacyRouteReason } from '../janus-engine/ability/orbs/orb-retired-route';
import { browserOnlyLegacyRouteReason } from '../janus-engine/ability/browser-only-layers';
import { ABILITY_ROW_BROWSER_ONLY_LAYERS, CASTER_ROW_BROWSER_ONLY_LAYERS, casterSpellLaneLegacyRouteReason, pageProviderReaders } from '../janus-engine/ability/caster-spell-layers';
import { casterStatusChoiceIds } from '../janus-engine/ability/statuses/caster-status-choice';
import { isShapeshiftPickVariable, SHAPESHIFT_LAYERS } from '../janus-engine/ability/shapeshift';
import { SUMMON_CANTRIPS_LAYER } from '../janus-engine/ability/summon-caster-cantrips';
import { readLayerListEntryUuid } from '../janus-engine/compile/inventory-raw-readers';
import { creatureControl } from '../janus-engine/ability/creature-control-ids';
import { pageSummonCantripLegacyMarker } from '../shared/summon-cantrips-browser';
import type {} from '../shared/painted-wall-wire';
import { actorCanReachTarget } from '../shared/proximity-browser';
import { pageInventoryRow } from '../shared/object-handling-browser';
import type {} from '../shared/aoe-propagation-wire';
import { refuseUnported } from '../janus-engine/unported';
import { abilityDeliveryFailureMessage, reportDeliveryThrow } from '../janus-engine/telemetry/delivery-throw';
import { inventoryWeaponSelected } from '../janus-engine/actions/cold-weapon-bind';
import { abilityVariableModeReason, isSpellGrantMode, type SpellGrantMode } from '../janus-engine/ability/spell-grant-mode';
import { isVariableAmount } from '../janus-engine/ability/variable-amount';
import { fabricationAllowsTargetlessCast } from '../janus-engine/ability/fabrication-targetless';
import { usesGroundSummonPlacement } from '../janus-engine/ability/ground-summon-placement';
import { browserAbilityGateVerdict, browserAreaAnchorVerdict, browserBounceVerdict, browserCastGrantVerdict, browserChainVerdict, browserCraftingWaiverHeld, browserHeldItemLineOfEffect, browserSelectionVerdict, browserTargetRuleRefusal,
    ensureAbilityGateData, ensureAbilityRequirementDefinitions } from './ability-preconditions-browser';
import { MAGIC_MISSILE_PARENT } from '../shared/magic-effect-contract';
import type {} from '../websocket/ft3d-magic-effect';

const w = window;

window.ftUseProjectileAssignments = async (casterId: string, abilityId: string, assignments: ProjectileAssignment[], instant) => {
    const context = getSidelogContext();
    if (!context || String(context.caster_id).toUpperCase() !== casterId.toUpperCase() || !assignments.length) return;
    let presentation;
    if (instant?.parentId === MAGIC_MISSILE_PARENT && new URLSearchParams(location.search).get('magicMissilePilot') === '1') {
        try {
            if (!window.ftPrepareMagicSelection) throw new Error('Magic Missile effects are unavailable. Refresh before casting.');
            presentation = window.ftPrepareMagicSelection(assignments.length);
        } catch (error) { displayWarning(String(error)); cancelTargetDraft('presentation-preflight'); return; }
    }
    // Every assigned CREATURE must be seen (cast-gate session 2026-09-29, R-103: PHP's per-assignment preflight no longer judges a
    // player's sight, and its batch refused the whole volley on one unseen target). Checked before either dispatch lane.
    const volleyCreatures = [...new Set(assignments.map(a => String(a.targetSelection).trim().toUpperCase())
        .filter(sel => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(sel)))];
    const volleyAbility = abilityBlob(abilityId);
    if (volleyCreatures.length && volleyAbility) {
        const sight = browserSelectionVerdict({ casterId, encounterId: String(context.encounter_id ?? ''), ability: volleyAbility,
            targetId: volleyCreatures.join(','), inCampaign: !!context.campaign_id, sightOnly: true });
        if (sight.kind === 'refuse' || sight.dropped.length) {
            displayWarning('You cannot target into an obscured area.');
            cancelTargetDraft('ability-request-refused');
            return;
        }
    }
    // The cast-legitimacy check (cast-grant-gate.ts, owner 2026-10-08): the volley's browser lane below never passes the dispatch gate, so the
    // PARENT spell is judged here before the browser volley. Unclassified → allowed (logged at debug level).
    if (instant) {
        const grant = browserCastGrantVerdict({ casterId, ability: abilityBlob(instant.parentId), variable1: instant.variable1,
            variable2: instant.variable2, encounterId: context.encounter_id });
        if (grant.kind === 'refuse') { displayWarning(grant.message); cancelTargetDraft('ability-request-refused'); return; }
    }
    // One parent cast and ordered child dispatches share the browser's single commit. Each area retains its own scene proof and mask.
    const uuid = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
    const areaChild = !!volleyAbility && ftLayerValue(volleyAbility, 'aoe_type') != null;
    if (instant && !presentation && typeof w.ftTryCommitAbilityUse === 'function' && context.encounter_id) {
        const selections = areaChild ? [] : assignments.map(a => String(a.targetSelection).trim().toUpperCase());
        if (!areaChild && assignments.some(a => a.propagation || !uuid.test(String(a.targetSelection).trim()))) {
            refuseUnported('volley:assignment-shape'); cancelTargetDraft('ability-request-refused'); return;
        }
        const areas = areaChild ? assignments.map(a => ({ targetId: a.targetSelection, propagation: a.propagation, placementProof: a.placementProof })) : undefined;
        let outcome: 'handled' | 'refused' | null = null;
        try {
            outcome = await w.ftTryCommitAbilityUse({ encounterId: String(context.encounter_id), actorId: casterId, targetIds: [...new Set(selections)],
                abilityId: instant.parentId, rawDiceMod: Number(context.raw_dice_mod) || 0, rawDiceAdvDisStacks: Number(context.raw_dice_adv_dis_stacks) || 0,
                rollPrivate: Number(context.roll_private) || 0,
                ...(isSpellGrantMode(instant.variable1) ? { spellGrant: { mode: instant.variable1, sourceId: instant.variable2 } } : {}),
                volley: { childId: abilityId, selections, ...(areas ? { areas } : {}), variable1: instant.variable1, variable2: instant.variable2 } });
        } catch (error) { reportDeliveryThrow(error); console.error('ftTryCommitAbilityUse (volley) failed', error); displayWarning(escapeHtml(abilityDeliveryFailureMessage(error))); outcome = 'refused'; }
        if (outcome !== null) { cancelTargetDraft('ability-request-complete'); return; }
    }
    if (instant && areaChild) return queryAbilityData('volley:area-lane-unavailable', {
        caster_id: casterId, item_id_used: instant.parentId, encounter_id: context.encounter_id });
    if (!presentation) openSideLog();
    return queryAbilityData('legacy-ability-route-removed', {
        caster_id: casterId,
        item_id_used: instant?.parentId ?? abilityId,
        ...(instant ? { use_ability_variable_1: instant.variable1, use_ability_variable_2: instant.variable2 } : {}),
        encounter_id: context.encounter_id,
    });
};

/** One-shot Quick Ritual opt-in (ritual_spell_mana_discount): the action-bar choice bar sets
 *  window.ftQuickRitualNextCast just before firing a NORMAL cast of a ritual spell; consuming it
 *  here marks that single cast for refusal until the Quick Ritual browser contract is ported.
 *  Consumed at call entry so an aborted validation can never leak the flag onto a later cast. */
function consumeQuickRitualFlag(): { quick_ritual?: number } {
    const holder = w as { ftQuickRitualNextCast?: boolean };
    if (holder.ftQuickRitualNextCast) {
        holder.ftQuickRitualNextCast = false;
        return { quick_ritual: 1 };
    }
    return {};
}

// Deterministic browser target gates reject incompatible selections before dispatch.
// Incomplete data reaches the named refusal chokepoint.
function clientTargetGateBlocksUse(
    item_id_used: unknown,
    caster_id: unknown,
    target_id: unknown,
    encounter_id: unknown,
): boolean {
    const parsed = w.parseTargetIdField?.(String(target_id ?? '')) ?? { entityIds: [] };
    // A map-point action owns an area, not a directly selected creature. Its occupants
    // are incidental even when exactly one remains; Janus filters their team/type after
    // validating the point. A single friendly occupant must not veto Earth Shock.
    if (parsed.mapPointToken) return false;
    const entityIds = Array.isArray(parsed?.entityIds) ? parsed.entityIds : [];
    const warn = w.abilityTeamGateWarning?.(item_id_used, caster_id, entityIds)
        ?? w.abilityTargetGateWarning?.(item_id_used, caster_id, entityIds);
    if (warn) {
        if (typeof displayWarning === 'function') displayWarning(warn);
        else console.warn(warn);
        return true;
    }
    // The item-type list / stowed-gear reach / war-machine hands (R-103): ahead of every client-truth lane, not only the legacy post.
    const rule = browserTargetRuleRefusal({ casterId: String(caster_id ?? ''), encounterId: String(encounter_id ?? ''), ability: abilityBlob(item_id_used), targetId: target_id });
    if (rule) {
        displayWarning(rule);
        return true;
    }
    // FT3D owns perception and target selection. The server remains authoritative for
    // visibility validation when the parent page has no synchronous FT3D visibility query.
    return false;
}

/**
 * The cast-time TARGET gates for a player's selection (cast-gate session 2026-09-29, R-103 — PHP no longer judges any of them for a
 * player): a creature-anchored area's anchor, the directed-sight drop (payload.target_id loses the unseen picks) and the count ceiling,
 * the item-type list / stowed gear / war-machine hands, a held item's line to its holder, and Bouncing's geometry. Resolves true when
 * refused (the warning is shown and the draft cancelled). Runs ahead of every browser lane (preLaneTargetGates).
 */
async function castTargetGatesRefuse(payload: Record<string, unknown>): Promise<boolean> {
    const selectionAbility = abilityBlob(payload.item_id_used);
    if (selectionAbility && payload.caster_id && payload.target_id) {
        // A creature-anchored area's picked anchor: seen, and a Burrowed one inside tremorsense with a clear line up (R-103).
        const anchor = await browserAreaAnchorVerdict({ casterId: String(payload.caster_id), encounterId: String(payload.encounter_id ?? ''),
            ability: selectionAbility, targetId: payload.target_id });
        if (anchor.kind === 'refuse') {
            displayWarning(anchor.message);
            if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-refused');
            return true;
        }
        const selection = browserSelectionVerdict({ casterId: String(payload.caster_id), encounterId: String(payload.encounter_id ?? ''),
            ability: selectionAbility, targetId: payload.target_id, variable1: payload.use_ability_variable_1, inCampaign: !!payload.campaign_id });
        if (selection.kind === 'refuse') {
            displayWarning(selection.message);
            if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-refused');
            return true;
        }
        if (selection.dropped.length) {
            payload.target_id = selection.targetId;
            const names = selection.dropped.map(name => name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')).join(', ');
            displayWarning(`Obscured target(s) skipped: ${names}`);
        }
        // The item-type list, reach into another creature's stowed gear, both hands free to pilot a war machine (R-103).
        const ruleRefusal = browserTargetRuleRefusal({ casterId: String(payload.caster_id), encounterId: String(payload.encounter_id ?? ''),
            ability: selectionAbility, targetId: payload.target_id });
        if (ruleRefusal) {
            displayWarning(ruleRefusal);
            if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-refused');
            return true;
        }
        // A held item's line of effect runs to its holder: the FT3D scene judges it (R-103).
        const heldLine = await browserHeldItemLineOfEffect({ casterId: String(payload.caster_id), encounterId: String(payload.encounter_id ?? ''),
            ability: selectionAbility, targetId: payload.target_id });
        if (heldLine.kind === 'refuse') {
            displayWarning(heldLine.message);
            if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-refused');
            return true;
        }
    }
    // Bouncing Spell geometry (cast-gate session 2026-09-29): the caster → primary range and the 10 ft gap are the browser's alone —
    // PHP no longer judges them (R-103). Refused here with PHP's message, before the precondition gate, in PHP's old order.
    const bounceAbility = abilityBlob(payload.item_id_used);
    const bounce = bounceAbility && payload.caster_id
        ? browserBounceVerdict({ casterId: String(payload.caster_id), encounterId: String(payload.encounter_id ?? ''), ability: bounceAbility,
            targetId: payload.target_id, variable1: payload.use_ability_variable_1 })
        : { kind: 'not-bounce' as const };
    if (bounce.kind === 'refuse') {
        displayWarning(bounce.message);
        if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-refused');
        return true;
    }
    // CHAIN geometry (chain_targets_max, 2026-10-07 — R-175): each leap pick within chain_distance_ft of the first pick.
    const chain = bounceAbility && payload.caster_id
        ? browserChainVerdict({ casterId: String(payload.caster_id), encounterId: String(payload.encounter_id ?? ''), ability: bounceAbility,
            targetId: payload.target_id })
        : { kind: 'not-bounce' as const };
    if (chain.kind === 'refuse') {
        displayWarning(chain.message);
        if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-refused');
        return true;
    }
    return false;
}

/** castTargetGatesRefuse ahead of a client-truth lane. Returns false SYNCHRONOUSLY for a map-point / empty selection (a placement
 *  dispatch must never yield before its FT3D placement is read); else resolves the refusal and the selection to dispatch. */
function preLaneTargetGates(fields: { item_id_used: unknown; caster_id: unknown; target_id: unknown; encounter_id: unknown;
    campaign_id?: unknown; use_ability_variable_1?: unknown; use_ability_variable_2?: unknown }): false | Promise<{ refused: boolean; targetId: unknown }> {
    const parsed = w.parseTargetIdField?.(String(fields.target_id ?? '')) ?? { mapPointToken: null, entityIds: [] };
    if (parsed.mapPointToken || !String(fields.target_id ?? '').trim() || !fields.caster_id) return false;
    const payload: Record<string, unknown> = { ...fields };
    return castTargetGatesRefuse(payload).then(refused => ({ refused, targetId: payload.target_id }));
}

const RANDOM_TABLE_ITEM_TYPES: ReadonlySet<string> = new Set(['random_table', 'random_loot_table', 'random_encounter_table', 'random_name_table']);

function abilityBlob(itemId: unknown): Record<string, unknown> | null {
    const key = String(itemId ?? '');
    try {
        const found = w.ftFindFullNestedItem?.(key);
        if (found && typeof found === 'object') return found as Record<string, unknown>;
    } catch {
        // Continue to the page-seeded object cache below.
    }

    const cached = (w.FateTide as { objectCache?: Map<string, unknown> } | undefined)?.objectCache?.get(key);
    return cached && typeof cached === 'object' ? cached as Record<string, unknown> : null;
}

function blobItemType(blob: Record<string, unknown> | null): string {
    if (!blob) return '';
    try {
        const itemType = String(ftItemsCol(blob, 'item_type') ?? '').toLowerCase();
        if (itemType) return itemType;
    } catch {
        // Fall through to the legacy flat shape.
    }
    return String(blob.item_type ?? '').toLowerCase();
}

/** All summon invocation shapes join the same cold creature read before their page gate. */
function ensureSummonPickDefinition(ability: Record<string, unknown>, pick: string): boolean | Promise<boolean> {
    if (ftLayerValue(ability, 'user_choice_1_summon') == null) return true;
    const pickId = pick.trim().toUpperCase();
    if (!(ftLayerEntries(ability, 'user_choice_1_summon') ?? [])
        .some(entry => readLayerListEntryUuid(entry).trim().toUpperCase() === pickId)) {
        displayWarning('Your summon selection choice is empty!');
        return false;
    }
    const held = (): boolean => {
        const definition = abilityBlob(pickId);
        const layers = definition?.item_layer_types;
        return !!layers && typeof layers === 'object' && !Array.isArray(layers)
            && Object.keys(layers).length > 0 && blobItemType(definition) === 'creature';
    };
    if (held()) return true;
    return (async () => {
        const fetchObject = (w as Window & { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
        try { await fetchObject?.(pickId); } catch { /* report the existing readiness refusal below */ }
        if (held()) return true;
        refuseUnported('summon:pick-definition-unavailable:' + pickId, { abilityId: String(ftItemsCol(ability, 'item_id')) });
        return false;
    })();
}

/** Wizard v9 WP3 (2026-10-09): the confirmed FT3D SUMMON placement this dispatch casts (FT3DAOEPlacement.startSummon — placementKind
 *  'summon', committed for THIS caster + ability + encounter), as the summon lane's input: the ground point of the selection
 *  (`<encounter>|x:y:z`) and the spots the caster banked one per summon (window.ftSummonPoints, fresh and bound to the same cast). Null =
 *  not a summon placement (the cast keeps its route). */
function summonPlacementOf(active: unknown, casterId: unknown, abilityId: unknown, encounterId: unknown, targetId: unknown):
    { point: { x: number; y: number; z: number }; points: Array<{ x: number; y: number; z: number }>; pick?: string } | null {
    const placement = active as { committed?: boolean; envelope?: { intent?: { placementKind?: string; casterId?: string; abilityId?: string;
        encounterId?: string; summonCreatureId?: string } | null } } | null | undefined;
    const intent = placement?.committed ? placement.envelope?.intent : null;
    const same = (a: unknown, b: unknown): boolean => String(a ?? '').toUpperCase() !== '' && String(a ?? '').toUpperCase() === String(b ?? '').toUpperCase();
    const placed = !!intent && intent.placementKind === 'summon' && same(intent.casterId, casterId) && same(intent.abilityId, abilityId)
        && same(intent.encounterId, encounterId);
    // …or an explicit ground point handed to a ground summon (ftUseAbilityAtExplicitTarget — a contextual command): the cast the legacy route
    // took for the same selection. A corpse-raiser never aims at the ground; another live placement keeps its own route.
    const ability = abilityBlob(abilityId);
    const groundSummon = !!ability && ftLayerValue(ability, 'summon_transforms_targeted_corpse') == null
        && (ftLayerValue(ability, 'summon') != null || ftLayerValue(ability, 'user_choice_1_summon') != null);
    // (Codex review 2026-10-09: ANY other placement in flight — committed or not — keeps the explicit-point fallback off)
    if (!placed && (placement || !groundSummon)) return null;
    const m = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\|(-?\d+(?:\.\d+)?):(-?\d+(?:\.\d+)?):(-?\d+(?:\.\d+)?)$/i
        .exec(String(targetId ?? '').replace(/,+$/, '').trim());
    if (!m || !same(m[1], encounterId)) return null;
    const point = { x: Number(m[2]), y: Number(m[3]), z: Number(m[4]) };
    const banked = placed ? window.ftSummonPoints : null;
    const points = banked && Date.now() - banked.atMs < 10000 && same(banked.casterId, casterId) && same(banked.abilityId, abilityId)
        ? banked.points.slice(0, 50).map(p => ({ x: Number(p.x), y: Number(p.y), z: Number(p.z) })) : [];
    const pick = placed && intent?.summonCreatureId && ftLayerValue(ability, 'user_choice_1_summon') != null
        ? intent.summonCreatureId : null;
    return [point, ...points].every(p => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z))
        ? { point, points, ...(pick ? { pick } : {}) } : null;
}

function cancelActiveFt3dPlacement(): void {
    // A synchronous input-based dispatch can reach here inside placement.commit().
    // That commit owns draft teardown and has already detached its internal active row;
    // cancelling it again would erase the freshly bound painted geometry.
    if (window.aoeManager?.ft3dActive && !window.aoeManager.ft3dActive.committed) window.aoeManager.cancelFt3d();
}

/** Every legacy dispatch stops here; retire the cast's target draft even on refusal. */
async function queryAbilityData(
    reason: string,
    payload: Record<string, unknown>,
): Promise<unknown> {
    const route = { encounterId: String(payload.encounter_id ?? ''), actorId: String(payload.caster_id ?? ''), abilityId: String(payload.item_id_used ?? ''),
        // the cast's variables (a level / a free-cast marker / a charged source): the Wizard v9 spell-lane guard judges THIS cast with them
        variable1: payload.use_ability_variable_1, variable2: payload.use_ability_variable_2 };
    // The shared adapter preserves the last named lane reason. Local readers retain layer-specific reasons when it is absent.
    const guard = (window as unknown as { ftRefuseLegacyRoute?: (input: typeof route, reason?: string) => boolean }).ftRefuseLegacyRoute;
    const routeAbility = abilityBlob(payload.item_id_used) as Record<string, unknown> | null;
    const casterRow = ((window.FateTide as { casterCache?: Map<string, unknown> } | undefined)?.casterCache?.get(route.actorId.toUpperCase()) ?? null) as Record<string, unknown> | null;
    const retired = orbLegacyRouteReason(routeAbility, ftLayerValue, 'page-legacy-dispatch')
        ?? browserOnlyLegacyRouteReason([routeAbility], ftLayerValue, 'page-legacy-dispatch', ABILITY_ROW_BROWSER_ONLY_LAYERS)
        ?? browserOnlyLegacyRouteReason([casterRow], ftLayerValue, 'page-legacy-dispatch', CASTER_ROW_BROWSER_ONLY_LAYERS)
        // Wizard v9 caster-side spell layers carried by a passive / feat / status, judged for THIS cast (caster-spell-layers.ts)
        ?? casterSpellLaneLegacyRouteReason(pageProviderReaders({ value: (r, k) => ftLayerValue(r, k), entries: (r, k) => ftLayerEntries(r, k) ?? [],
            statuses: (r) => (ftCreatureStatuses(r, 'target') ?? []) as unknown[], definition: (id) => abilityBlob(id) as Record<string, unknown> | null }),
            casterRow, routeAbility, { variable1: payload.use_ability_variable_1, variable2: payload.use_ability_variable_2 }, 'page-legacy-dispatch')
        // summons_gain_caster_cantrips: a summon's injected cantrip (summon-cantrips-browser.ts) never runs on the PHP route
        ?? browserOnlyLegacyRouteReason([pageSummonCantripLegacyMarker(casterRow, routeAbility)], ftLayerValue, 'page-legacy-dispatch',
            new Set([SUMMON_CANTRIPS_LAYER]));
    try {
        if (guard?.(route, reason === 'legacy-ability-route-removed' ? undefined : reason) === true) return;
        const fallback = reason === 'legacy-ability-route-removed' && (route.variable1 != null || route.variable2 != null)
            ? abilityVariableModeReason(route.variable1, route.variable2) : reason;
        return refuseUnported(retired ?? fallback, route);
    } finally {
        w.ftThrowMode = null;
        cancelTargetDraft('legacy-route-refused');
    }
}

// Sheet ability buttons: single tap / click activates immediately. "Open details" is handled by
// the press-hold system (setupSheetAbilityPressForInfo) which calls openItemInfo on ~450ms hold
// and suppresses the trailing click via a capture-phase listener, so these wrappers no longer
// need a double-click window or a setTimeout delay.
// TODO: these are pure passthroughs now. Inline at HTML/PHP onclick call sites and delete the
// wrappers in a follow-up pass once all callers are confirmed within this repo.
function handleAbilityBtnSingleClick(itemId: unknown)                                                              { useAbility(itemId); }
function handleuseAbilityVariable1BtnSingleClick(itemId: unknown, use_ability_variable_1: unknown)                     { useAbilityVariable1(itemId, use_ability_variable_1); }
function handleuseAbilityVariable2BtnSingleClick(itemId: unknown, use_ability_variable_1: unknown, use_ability_variable_2: unknown)  { useAbilityVariable2(itemId, use_ability_variable_1, use_ability_variable_2); }
function handleuseAbilityInput1BtnSingleClick(itemId: unknown, use_ability_input_1: unknown)                           { useAbilityInput1(itemId, use_ability_input_1); }
function handleuseAbilityInput1Input2BtnSingleClick(itemId: unknown, use_ability_input_1: unknown, use_ability_input_2: unknown) { useAbilityInput1Input2(itemId, use_ability_input_1, use_ability_input_2); }

/** A contextual command whose target is already inherent in the clicked card/object. Project that one
 * explicit target only while assembling this ability request; never pre-seed ambient selection before
 * the action is chosen. */
async function ftUseAbilityAtExplicitTarget(
    abilityId: unknown,
    targetId: unknown,
    variable1?: unknown,
): Promise<void> {
    if (String(abilityId).toUpperCase() === '35B277D9-F5E7-14A7-D2B1-9326C410549A') {
        const within = await actorCanReachTarget(String(window.ftGetActiveCasterId?.() ?? ''), String(targetId ?? ''));
        if (within !== true) {
            displayWarning(within === false ? 'Move within reach to peek in bags.' : 'Could not determine reach. Refresh the encounter and try again.');
            return;
        }
    }
    w.ftSetTargetSelection?.(String(targetId ?? ''), 'explicit-ability-target');
    try {
        if (variable1 === undefined) await useAbility(abilityId);
        else await useAbilityVariable1(abilityId, variable1);
    } finally {
        if (typeof cancelTargetDraft === 'function') cancelTargetDraft('explicit-ability-request-complete');
    }
}


/** A cast PHP's validate_and_adjust_targets puts on its CASTER whatever was selected: a Self range (D82855E1…), for the root item types the
 *  ability oracle re-enacts as `self` keys (tools/probes/ability_use_oracle_capture.php AO_ROOT_TYPES — the census judges that exact input).
 *  Lane A6 (2026-10-01, OWNER ANSWER 2 "complete the conversion"): a reaction ability used BY HAND joins them — the real-PHP capture of every
 *  hand-used reaction in the 90-day corpus (`tools/test/artifacts/janus-ability-oracle-2026-10-01-a6handreact`, ability_use_oracle_capture.php
 *  --hand-reactions --hold-charge-entry, base + combat) judges WRONG 0. Direct creature controls use their explicit self-subject lane. */
const SELF_CAST_ROOT_TYPES = new Set(['spell', 'ability', 'quick_ability', 'full_ability', 'free_ability', 'menta', 'sanguine', 'reaction_ability']);
function selfRangeCast(ability: unknown, casterId?: unknown): boolean {
    if (!ability) return false;
    const extension = (window as Window & { ftCastRangeExtension?: (casterId: string, ability: unknown) =>
        { unknown: string } | { rangeId: string } | null }).ftCastRangeExtension?.(String(casterId ?? ''), ability);
    if (extension && !('unknown' in extension) && extension.rangeId !== 'D82855E1-4BB9-93EA-6374-B9904318BC1D') return false;
    return String(ftLayerValue(ability, 'range') ?? '').toUpperCase() === 'D82855E1-4BB9-93EA-6374-B9904318BC1D'
        && SELF_CAST_ROOT_TYPES.has(String(ftItemsCol(ability, 'item_type') ?? '').toLowerCase());
}

/** ability_uses_caster_inventory_target_picker() (get_weapon_for_attack_ability.php): an item-type gate with no team-targeting layer — the target is a
 *  carried item, never the caster. (PHP also needs a non-empty allowed-type list; treating every such gate as a picker only keeps more casts
 *  off the forced-caster path — conservative.) */
function ownItemPicker(ability: unknown): boolean {
    if (!ability || ftLayerValue(ability, 'ability_works_only_against_item_type') == null) return false;
    if (['effect_only_affects_opposing_team_type', 'effect_only_affects_same_team_type', 'target_opposing_team_only', 'target_friendly_only']
        .some(key => ftLayerValue(ability, key) != null)) return false;
    // caster_inventory_target_allowed_types(): a gate naming ONLY world objects is not an own-item picker (Codex A8 r3 P2)
    const NON_CARRYABLE = new Set(['furniture', 'door', 'building', 'window', 'wall', 'roof', 'floor']);
    const listed = ((window as unknown as { ftLayerEntries?: (r: unknown, k: string) => unknown[] }).ftLayerEntries?.(ability, 'ability_works_only_against_item_type') ?? [])
        .map(e => String(e ?? '').trim().toLowerCase()).filter(t => t !== '' && !NON_CARRYABLE.has(t));
    return listed.length > 0;
}

/** RollType crafting_roll — a recipe's use with no variable_1 is a craft START (crafting-start.ts). */
const ROLL_CRAFTING_ID = '54DC30E0-923C-4BBF-A8CC-570B0068E8A2';
/** The End Concentration ability (info-panel/creature-action-buttons.ts renders one button per Concentrating row). */
const END_CONCENTRATION_ABILITY = '5496F09C-94BB-452E-9DDD-47CC871CFB5E';
/** Direct controls have an explicit self subject; they do not consult the ambient target draft. */
async function creatureControlLane(abilityId: unknown, actorId: unknown, encounterId: unknown, rollPrivate: unknown): Promise<boolean> {
    if (!creatureControl(abilityId)) return false;
    const ability = String(abilityId).toUpperCase();
    try {
        if (!actorId || !encounterId || typeof w.ftTryCommitAbilityUse !== 'function') {
            refuseUnported('summon-control:actor-or-browser-unavailable');
            return true;
        }
        if (!abilityBlob(ability)) {
            const fetchObject = (w as Window & { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
            if (fetchObject) await fetchObject(ability);
        }
        const definition = abilityBlob(ability);
        if (!definition) { refuseUnported('summon-control:definition-not-held'); return true; }
        const gathered = await ensureAbilityGateData({ casterId: String(actorId), abilityId: ability, encounterId });
        const gate = browserAbilityGateVerdict({ casterId: String(actorId), encounterId: String(encounterId), ability: definition, gathered });
        if (gate.kind === 'refuse') { displayWarning(gate.message); return true; }
        if (gate.kind !== 'allow') { refuseUnported('summon-control:gate-' + gate.site); return true; }
        const outcome = await w.ftTryCommitAbilityUse({ encounterId: String(encounterId), actorId: String(actorId),
            targetIds: [String(actorId)], abilityId: ability, rollPrivate: Number(rollPrivate) });
        if (outcome === null) refuseUnported('summon-control:browser-resolution-unavailable');
    } catch (error) {
        reportDeliveryThrow(error);
        console.error('Creature control delivery failed', error);
        displayWarning(escapeHtml(abilityDeliveryFailureMessage(error)));
    } finally { cancelTargetDraft('ability-request-complete'); }
    return true;
}
type CraftStartWire = (input: { actorId: string; encounterId?: string; recipe: Record<string, unknown>; sizeId?: string | null; rollPrivate?: number;
    gate: (encounterId: string) => Promise<{ kind: 'refuse'; message: string } | { kind: 'allow' } | { kind: 'unknown'; site: string }> })
    => Promise<{ outcome: 'handled' | 'refused' } | { outcome: 'handback'; reason: string }>;
/**
 * The CRAFT START lane (Wizard v9 WP6, 2026-10-09): a recipe use decided in the browser and committed as `crafting_start`
 * (websocket/status-truth.ts commitCraftStart → status_truth_commit_inc.php). true = the click is finished here (started, or refused with
 * words); false = an unsupported start that reaches the named refusal, unless the crafter holds Universal Crafter (crafting_waives_nonmagical_reagents),
 * which PHP never learns: that start is refused with the reason instead.
 */
async function craftStartLane(input: { casterId: string; encounterId: string; recipeId: unknown; sizeId: string | null; rollPrivate: unknown;
    variable1?: unknown }): Promise<boolean> {
    let recipe = abilityBlob(input.recipeId)
        ?? (w as Window & { ftResolveCachedInventoryRow?: (id: string) => Record<string, unknown> | null })
            .ftResolveCachedInventoryRow?.(String(input.recipeId ?? '')) ?? null;
    // Held inventory and catalog rows already identify non-recipes. Only unknown ids need a definition read;
    // the Crafting tab can start a recipe whose definition the page has not loaded yet.
    const fetchObject = (w as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
    if (!recipe && typeof fetchObject === 'function' && /^[0-9a-f-]{36}$/i.test(String(input.recipeId ?? ''))) {
        try {
            const fetched = await fetchObject(String(input.recipeId));
            recipe = (fetched && typeof fetched === 'object' ? fetched as Record<string, unknown> : null) ?? abilityBlob(input.recipeId) as Record<string, unknown> | null;
        } catch { recipe = null; }
    }
    if (!recipe || String(ftLayerValue(recipe, 'roll_type') ?? '').toUpperCase() !== ROLL_CRAFTING_ID) return false;
    await ensureAbilityRequirementDefinitions(recipe);
    // crafting_roll's start arm = PHP empty($use_ability_variable_1) (isCraftStart's predicate): null / '' / 0 / '0' / false
    const v1 = input.variable1;
    if (!(v1 === undefined || v1 === null || v1 === '' || v1 === 0 || v1 === '0' || v1 === false)) return false;
    const commit = (w as unknown as { ftCommitCraftStart?: CraftStartWire }).ftCommitCraftStart;
    const route = { encounterId: input.encounterId, actorId: input.casterId, abilityId: String(input.recipeId) };
    let reason = 'crafting_start:lane-unavailable';
    if (typeof commit === 'function' && input.casterId) {
        try {
            const out = await commit({ actorId: input.casterId, encounterId: input.encounterId || undefined, recipe, sizeId: input.sizeId,
                rollPrivate: Number(input.rollPrivate) || 0,
                gate: async (encounterId) => {
                    const gathered = await ensureAbilityGateData({ casterId: input.casterId, abilityId: input.recipeId, encounterId,
                        variable1: undefined });
                    return browserAbilityGateVerdict({ casterId: input.casterId, encounterId, ability: recipe, gathered,
                        ...(input.sizeId ? { variable2: input.sizeId } : {}) });
                } });
            if (out.outcome !== 'handback') return true;
            reason = out.reason;
        } catch (error) {
            console.error('Craft start lane failed', error);
            displayWarning('The craft could not be started. Try again.');
            return true;
        }
    }
    w.ftReportAbilityRoute?.(route, reason);
    // crafting_waives_nonmagical_reagents is browser-only: the legacy route would ignore it (consume the waived materials, or refuse the craft).
    // Only a recipe with a waivable (untemplated) requirement is affected; an unprovable waiver counts as held.
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const waivable = ((w as unknown as { ftLayerEntries?: (r: unknown, k: string) => unknown[] }).ftLayerEntries?.(recipe, 'requires_reagent') ?? []).some(entry => {
        if (entry && typeof entry === 'object') return !uuid.test(String((entry as Record<string, unknown>).template_id ?? '').trim());
        const tail = String(entry ?? '').split('|')[1] ?? '';
        return String(entry ?? '').trim() !== '' && !uuid.test(tail.trim());
    });
    if (waivable && browserCraftingWaiverHeld(input.casterId) !== false) {
        displayWarning(`This craft cannot be started right now (${reason}). Reload the page and try again.`);
        return true;
    }
    return false;
}

/** A CHARGED-ITEM cast (Wizard v9 WP5 — janus-engine/ability/charged-cast.ts): use_ability_variable_1 = the mode, variable_2 = the source
 *  inventory row (wand / staff / scroll). useAbilityVariable2() routes it through useAbility() so it reaches the generic browser lane. */
type ChargedCast = { mode: 'charged_spell' | 'charged_ability'; inventoryId: string };
const CHARGED_SOURCE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function chargedCastOf(variable1: unknown, variable2: unknown): ChargedCast | null {
    return (variable1 === 'charged_spell' || variable1 === 'charged_ability') && typeof variable2 === 'string' && CHARGED_SOURCE_UUID.test(variable2.trim())
        ? { mode: variable1, inventoryId: variable2.trim() } : null;
}

/** `castLevel`: the numeric slot-1 variable, either a level or an authored X amount — the FT3D placement's picker routes a
 *  placed area here (useAbilityVariable1) so the placed lane below carries the level; the lane's resolver judges the gate WITH it (upcast.ts). */
async function useAbility(item_id_used: unknown, consume_inventory_id: string | null = null, charged: ChargedCast | null = null, attackMode: 'throw_weapon' | null = null,
    castLevel: string | null = null, damageTypePick: string | null = null, spellGrant: SpellGrantMode | null = null, spellGrantSource?: string,
    throwObject: { inventoryId: string; lob: boolean } | null = null, consumeInventoryUnit = false, summonPick: string | null = null, ritualSummon = false) {
    const finishTimingInput = beginActionTimingInput();
    try {
    // Open side log
    openSideLog();

    // a charged-item cast consumes the one-shot throw mode the way useAbilityVariable2 always did (served or not)
    if (charged || attackMode || throwObject) w.ftThrowMode = null;

    // Preserve the confirmed pre-cast draft while this request payload is assembled. queryAbilityData()
    // retires that draft only after the request settles, so the current cast keeps its exact targets and
    // the next ability always begins targetless.

    const sl_context = getSidelogContext();
    if (!sl_context) {
        refuseUnported('ability-use:context-unavailable', undefined, 'Reopen this character\'s sheet before using an ability.');
        return;
    }

    if (!consume_inventory_id && !charged && !attackMode && !throwObject
        && w.ftArmInstantProjectileSpell?.(String(sl_context.caster_id ?? ''), String(item_id_used ?? ''),
            castLevel ?? undefined, spellGrant ?? '', damageTypePick ?? '', spellGrantSource)) return;

    const {
        roll_private,
        campaign_id,
        encounter_id: viewedEncounterId,
        caster_id,
        target_id: selected_target_id,
        raw_dice_mod,
        raw_dice_adv_dis_stacks,
    } = sl_context;
    let target_id = selected_target_id;
    const summonAbility = abilityBlob(item_id_used);
    const dispatchSummon = (w as Window & { ftDispatchAbilityClick?: (casterId: string, abilityId: string,
        opts?: { deferredDispatch: () => void }) => boolean }).ftDispatchAbilityClick;
    if (!window.aoeManager?.ft3dActive?.committed
        && !w.parseTargetIdField?.(String(target_id ?? ''))?.mapPointToken
        && usesGroundSummonPlacement(summonAbility)
        && dispatchSummon?.(String(caster_id), String(item_id_used), ritualSummon || charged || consume_inventory_id || spellGrant || castLevel !== null || damageTypePick !== null ? { deferredDispatch: () => {
            void useAbility(item_id_used, consume_inventory_id, charged, attackMode, castLevel, damageTypePick,
                spellGrant, spellGrantSource, throwObject, consumeInventoryUnit, summonPick, ritualSummon);
        } } : undefined)) return;
    const quick_ritual_fields = consumeQuickRitualFlag();

    // Validate required fields
    const rules = {
        roll_private: { type: 'enum', value: roll_private, allowed: ['0', '1', '2'] },
        raw_dice_mod: { type: 'number', value: raw_dice_mod },
        raw_dice_adv_dis_stacks: { type: 'number', value: raw_dice_adv_dis_stacks },
        item_id_used: { type: 'uuid', value: item_id_used },
    };
    const validationResult = validateContext(rules, 'useAbility');
    if (!validationResult.valid) {
        refuseUnported('ability-use:invalid-context', { error: validationResult.error });
        return;
    }
    // Capture the confirmed draft before the first await; confirmation retires the manager's live mirror as dispatch yields.
    const placementAtDispatch = window.aoeManager?.ft3dActive ?? null;
    const summonAtDispatch = summonPlacementOf(placementAtDispatch, caster_id, item_id_used, viewedEncounterId, target_id);
    if (summonAtDispatch && summonPick) summonAtDispatch.pick ??= summonPick;
    const encounter_id = await skillRollEncounterId(String(viewedEncounterId ?? ''), String(caster_id ?? ''), String(item_id_used));
    if (typeof encounter_id !== 'string') {
        refuseUnported(encounter_id.reason, undefined, encounter_id.message);
        cancelTargetDraft('ability-request-refused');
        return;
    }
    if (throwObject && !placementAtDispatch?.committed) { displayWarning('Choose where to throw it.'); return; }
    if (throwObject && !abilityBlob(item_id_used)) {
        const fetchObject = (w as Window & { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
        if (fetchObject) await fetchObject(String(item_id_used).toUpperCase());
    }
    if (!consume_inventory_id && !charged && await creatureControlLane(item_id_used, caster_id, encounter_id, roll_private)) return;

    // A CRAFT START (Wizard v9 WP6): a recipe is started by the browser (craftStartLane); unsupported starts reach the named refusal.
    if (!throwObject && !consume_inventory_id && await craftStartLane({ casterId: String(caster_id ?? ''), encounterId: String(encounter_id ?? ''), recipeId: item_id_used,
        sizeId: null, rollPrivate: roll_private })) return;

    // A RANDOM TABLE used directly (Lane N2 RT-3, 2026-10-01): the browser rolls it (websocket/roll-truth.ts → roll_truth_commit_inc.php
    // kind `table`); a missing browser bundle reaches the named refusal below.
    if (typeof w.ftTryCommitTableRoll === 'function' && (RANDOM_TABLE_ITEM_TYPES.has(blobItemType(abilityBlob(item_id_used))) || w.ftHeldRandomTableDef?.(item_id_used))) {
        const firstTarget = String(target_id ?? '').split(/[,\s]+/).find(v => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) ?? '';
        const outcome = await w.ftTryCommitTableRoll(item_id_used, { campaign_id, encounter_id, caster_id, roll_private, target_id: firstTarget });
        if (outcome !== null) return;
    }

    if (clientTargetGateBlocksUse(item_id_used, caster_id, target_id, encounter_id)) return;

    // The cast-legitimacy check (cast-grant-gate.ts, owner 2026-10-08) ahead of the self-status / weapon-attack lanes below, which never
    // reach the dispatch gate (browserAbilityGateVerdict runs it for every other lane). Unclassified → allowed (logged at debug level).
    const castGrant = browserCastGrantVerdict({ casterId: String(caster_id ?? ''), ability: abilityBlob(item_id_used), consumeInventoryId: consume_inventory_id,
        encounterId: encounter_id, ...(throwObject ? { variable1: 'throw_inventory', variable2: throwObject.inventoryId }
            : charged || spellGrant ? { variable1: charged?.mode ?? spellGrant } : {}) });
    if (castGrant.kind === 'refuse') {
        displayWarning(castGrant.message);
        if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-refused');
        return;
    }

    // The cast-time TARGET gates (R-103) ahead of every client-truth lane; the unseen picks they drop leave this dispatch's selection.
    const targetGates = preLaneTargetGates({ item_id_used, caster_id, target_id, encounter_id, campaign_id,
        use_ability_variable_1: ritualSummon ? 'spell_ritual' : throwObject ? 'throw_inventory' : spellGrant ?? castLevel ?? damageTypePick ?? attackMode ?? undefined,
        ...(throwObject ? { use_ability_variable_2: throwObject.inventoryId } : spellGrantSource ? { use_ability_variable_2: spellGrantSource } : castLevel !== null && damageTypePick !== null ? { use_ability_variable_2: damageTypePick } : {}) });
    if (targetGates) { const gated = await targetGates; if (gated.refused) return; target_id = gated.targetId as typeof target_id; }

    // Refuse a picked hurl before any mutation lane can spend its cast.
    try {
        w.ftHurlBind?.({ casterId: String(caster_id ?? ''), abilityId: String(item_id_used), encounterId: String(encounter_id ?? ''),
            targetSelection: String(target_id ?? '') });
    } catch (error) {
        if ((error as { name?: string })?.name === 'HurlBindingRefused') return;
        throw error;
    }

    // Only full abilities enter the self-status lane; an unheld definition may still hydrate Dodge.
    // Other item types, grants and charged casts use the generic lane. A handled/refused click retires its draft.
    if (typeof w.ftTryCommitSelfStatus === 'function' && encounter_id && caster_id && !consume_inventory_id && !throwObject && !charged && !spellGrant && castLevel === null && damageTypePick === null
        && ['', 'ability'].includes(blobItemType(abilityBlob(item_id_used)))
        && ftLayerValue(abilityBlob(item_id_used), 'change_teleport_remaining') == null) {
        let outcome: 'handled' | 'refused' | null = null;
        try {
            outcome = await w.ftTryCommitSelfStatus({ encounterId: String(encounter_id), actorId: String(caster_id), abilityId: String(item_id_used), rollPrivate: Number(roll_private) });
        } catch (error) {
            console.error('ftTryCommitSelfStatus failed; continuing to the browser refusal', error);
            outcome = null;
        }
        if (outcome !== null) { cancelActiveFt3dPlacement(); if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-complete'); return; }
    }

    // A plain weapon slot uses its exact inventory id; the attack adapter binds
    // that instance before drawing and saves through the shared attack outbox.
    const weaponRoster = (w.FateTide?.encounterCache?.get(String(encounter_id).toUpperCase()) as
        { encounter_creatures_arr?: Record<string, unknown>[] } | undefined)?.encounter_creatures_arr;
    const weaponActor = weaponRoster?.find(row =>
        String(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_id')).toUpperCase() === String(caster_id).toUpperCase())
        ?? w.FateTide?.casterCache?.get(String(caster_id).toUpperCase()) ?? null;
    const inventoryWeapon = inventoryWeaponSelected(weaponActor as Record<string, unknown> | null, String(item_id_used))
        || String(ftInventoryField(abilityBlob(item_id_used), 'inventory_id') ?? '').toUpperCase() === String(item_id_used).toUpperCase();
    const weaponAttack = inventoryWeapon || ['weapon', 'natural_weapon'].includes(String(ftItemsCol(abilityBlob(item_id_used), 'item_type') ?? ''));
    let weaponUnavailableReason = typeof w.ftTryCommitWeaponAttack !== 'function' ? 'adapter-unavailable'
        : !encounter_id ? 'encounter-unavailable' : !caster_id ? 'attacker-unavailable'
        : charged ? 'charged-mode-unavailable' : spellGrant ? 'spell-grant-mode-unavailable'
        : castLevel !== null ? 'cast-level-unavailable' : damageTypePick !== null ? 'damage-type-pick-unavailable' : null;
    if (weaponAttack && typeof w.ftTryCommitWeaponAttack === 'function' && encounter_id && caster_id && !throwObject && !charged && !spellGrant && castLevel === null && damageTypePick === null) {
        const parsedTargets = w.parseTargetIdField?.(String(target_id ?? '')) ?? { mapPointToken: null, entityIds: [] };
        const targetIds = Array.isArray(parsedTargets.entityIds) ? (parsedTargets.entityIds as unknown[]).map(v => String(v ?? '').trim()).filter(Boolean) : [];
        if (targetIds.length === 1 && !parsedTargets.mapPointToken) {
            let outcome: 'handled' | 'refused' | null = null;
            try {
                outcome = await w.ftTryCommitWeaponAttack({
                    encounterId: String(encounter_id), actorId: String(caster_id), targetId: String(targetIds[0]), weaponId: String(item_id_used), attackMode,
                    rawDiceMod: Number(raw_dice_mod) || 0, rawDiceAdvDisStacks: Number(raw_dice_adv_dis_stacks) || 0, rollPrivate: Number(roll_private),
                });
            } catch (error) {
                console.error('ftTryCommitWeaponAttack failed; continuing to the browser refusal', error);
                weaponUnavailableReason = String(error instanceof Error ? error.message : error);
                outcome = null;
            }
            if (outcome !== null) { cancelActiveFt3dPlacement(); if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-complete'); return; }
        } else weaponUnavailableReason = parsedTargets.mapPointToken ? 'map-point-target-unavailable'
            : targetIds.length > 1 ? 'target-count' : 'target-unavailable';
    }
    if (!throwObject && inventoryWeapon) {
        const reason = weaponUnavailableReason ?? 'attack-state-unavailable';
        refuseUnported(reason.startsWith('weapon:') ? reason : 'weapon:' + reason, { weaponId: String(item_id_used) });
        cancelActiveFt3dPlacement();
        if (typeof cancelTargetDraft === 'function') cancelTargetDraft('ability-request-complete');
        return;
    }

    // Ordinary and spell-grant casts share the targeting and commit lane after the browser gate proves ALLOW.
    // Lane A5 (2026-10-01): WHY a cast reaches the refusal before the lane decides it — recorded as an adapter event ('skipped',
    // route:<reason>) so a playtest names every silent route (09-30: 79 of 107 real casts left no trace).
    const grantLaneAvailable = !spellGrant || (w as unknown as { ftAbilityLaneFeatures?: { spellGrant?: boolean } }).ftAbilityLaneFeatures?.spellGrant === true;
    let legacyRoute: string | null = Object.keys(quick_ritual_fields).length ? 'quick-ritual'
        : !grantLaneAvailable ? abilityVariableModeReason(spellGrant) : null;
    let laneDecided = false;
    if (encounter_id && caster_id) await ensureLaneEncounterForBrowserOnly(encounter_id, item_id_used, caster_id); // lane PC1
    if (typeof w.ftTryCommitAbilityUse === 'function' && encounter_id && caster_id
        && grantLaneAvailable && Object.keys(quick_ritual_fields).length === 0) {
        const parsedTargets = w.parseTargetIdField?.(String(target_id ?? '')) ?? { mapPointToken: null, entityIds: [] };
        let targetIds = Array.isArray(parsedTargets.entityIds) ? (parsedTargets.entityIds as unknown[]).map(v => String(v ?? '').trim()).filter(Boolean) : [];
        // Lane A5 (2026-10-01, playtest 09-30: 79 of 107 real ability casts never reached the lane, the largest group a Self-range cast with
        // nothing selected): validate_and_adjust_targets forces a Self range onto its caster, so any Self-range cast with no selection is one.
        // A Self follow AURA (Heat Vent) resolves on its caster too, whatever its item type (Janus Session 1 step 2, 2026-09-29).
        const selfAuraBlob = abilityBlob(item_id_used);
        // Legacy validate_and_adjust_targets §3: a rangeless consumable in a campaign defaults an empty selection to its caster.
        if (consume_inventory_id && campaign_id && !targetIds.length && !parsedTargets.mapPointToken && selfAuraBlob
            && ftLayerValue(selfAuraBlob, 'range') == null
            && (ftLayerValue(selfAuraBlob, 'consumable') != null || ['potion', 'elixir', 'food', 'drink'].includes(blobItemType(selfAuraBlob)))) {
            targetIds = [String(caster_id)];
            target_id = String(caster_id);
        }
        const selfAura = !!selfAuraBlob && String(ftLayerValue(selfAuraBlob, 'range') ?? '').toUpperCase() === 'D82855E1-4BB9-93EA-6374-B9904318BC1D'
            && ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(key => ftLayerValue(selfAuraBlob, key) != null);
        // Lane A7 (2026-10-02, coordinator Codex A6 V4 P2): a Self-range cast lands on its CASTER whatever the user had selected (owner answer 2:
        // a hand-used Self reaction enters with the caster as target) — the selection used to be sent when one existed. Exactly PHP's
        // validate_and_adjust_targets L1690: a Self cast WITHOUT an area (an area's selection is its victims — Thunderous Blast; Codex A7
        // review 1 P2) and not an own-item picker (ability_works_only_against_item_type: the target is a carried item).
        // Lane A8 (coordinator Codex A7 W2 P2s): a Self follow AURA is forced onto its caster WHATEVER is selected (validate_and_adjust_targets'
        // aura block, ~L320: check_item_arr_aura_effect + Self range → [caster]; the engine expands it); an own-item PICKER is never forced — with
        // nothing selected PHP resolves its default carried item (resolve_caster_default_target_for_ability), so it stays off the lane (no-target).
        if (!parsedTargets.mapPointToken && (selfAura || (selfRangeCast(selfAuraBlob, caster_id) && !ownItemPicker(selfAuraBlob)
            && (!targetIds.length || ftLayerValue(selfAuraBlob, 'aoe_type') == null)))) targetIds = [String(caster_id)];
        const activePlacement = placementAtDispatch as { committed?: boolean; staged?: Ft3dAoePlacementStageEnvelope | null;
            envelope?: Ft3dAoePlacementEnvelope } | null;
        const stage = activePlacement?.committed ? activePlacement.staged : null;
        const placed = parsedTargets.mapPointToken && stage?.targetSelection === String(target_id ?? '')
            && stage.placed === true && stage.version === activePlacement?.envelope?.version
            && activePlacement?.envelope?.intent?.placementKind === 'aoe'
            && String(activePlacement.envelope.intent.casterId ?? '').toUpperCase() === String(caster_id).toUpperCase()
            && String(activePlacement.envelope.intent.abilityId ?? '').toUpperCase() === String(item_id_used).toUpperCase()
            && String(activePlacement.envelope.intent.encounterId ?? '').toUpperCase() === String(encounter_id).toUpperCase()
            // The shared placement contract names the shapes; the finalizer re-proves their geometry and effect support.
            && isFt3dAoeShape(activePlacement.envelope.intent.shape) && typeof activePlacement.envelope.intent.persistent === 'boolean'
            && Boolean(activePlacement.envelope.intent.wallAttachment) === Boolean(stage.wallGeometry)
            && !window.ftSummonPoints
            ? { targetId: String(target_id),
                ...(stage.wallGeometry ? { paintedWall: { casterId: String(caster_id), abilityId: String(item_id_used),
                    targetSelection: stage.targetSelection, geometry: stage.wallGeometry } } : {}),
                ...(stage.propagation ? { propagation: { casterId: String(caster_id), abilityId: String(item_id_used), targetSelection: stage.targetSelection, mask: stage.propagation } } : {}) }
            : null;
        if (throwObject && !placed) { displayWarning('Choose where to throw it.'); return; }
        // Wizard v9 WP3: a confirmed FT3D SUMMON placement — the summon lane (janus-engine/ability/summon.ts) casts at its ground point
        const summonPlacement = placed ? null : summonAtDispatch;
        // Skill rolls and carryable fabrication casts can enter with an empty action-owned selection.
        const skillCheck = /_skill$/.test(blobItemType(selfAuraBlob));
        let targetlessFabrication = false;
        if (selfAuraBlob && !targetIds.length && !parsedTargets.mapPointToken && !placed && !summonPlacement
            && (ftLayerEntries(selfAuraBlob, 'fabricate') ?? []).length) {
            await ensureAbilityRequirementDefinitions(selfAuraBlob);
            targetlessFabrication = fabricationAllowsTargetlessCast(selfAuraBlob, charged?.mode ?? spellGrant ?? castLevel ?? null,
                { value: ftLayerValue, entries: (row, key) => ftLayerEntries(row, key) ?? [], definition: abilityBlob });
        }
        legacyRoute = parsedTargets.mapPointToken && !placed && !summonPlacement ? 'map-point-unplaced' : !targetIds.length && !placed && !summonPlacement && !skillCheck && !targetlessFabrication ? 'no-target'
            : window.aoeManager?.ft3dActive && !placed && !summonPlacement ? 'placement-active' : null;
        if ((!parsedTargets.mapPointToken && (targetIds.length > 0 || skillCheck || targetlessFabrication) && !window.aoeManager?.ft3dActive) || placed || summonPlacement) {
            const ability = abilityBlob(item_id_used);
            if (!ability) legacyRoute = 'definition-not-held';
            if (ability) {
                if (summonPlacement?.pick) {
                    const readiness = ensureSummonPickDefinition(ability, summonPlacement.pick);
                    if (!(typeof readiness === 'boolean' ? readiness : await readiness)) {
                        window.ftSummonPoints = null; cancelActiveFt3dPlacement(); cancelTargetDraft('ability-request-refused'); return;
                    }
                }
                // Gather and judge the same chosen creature, including cold definitions and source-aware casts.
                const variables = {
                    ...(ritualSummon ? { variable1: 'spell_ritual' }
                        : consume_inventory_id && castLevel === null && damageTypePick === null ? { variable1: 'nested_inventory_source', variable2: consume_inventory_id } : throwObject ? { variable1: 'throw_inventory', variable2: throwObject.inventoryId }
                        : charged ? { variable1: charged.mode, variable2: charged.inventoryId }
                        : spellGrant ? { variable1: spellGrant, ...(spellGrantSource ? { variable2: spellGrantSource } : {}) }
                        : castLevel !== null ? { variable1: castLevel, ...(damageTypePick !== null ? { variable2: damageTypePick } : {}) } : {}),
                    ...(summonPlacement?.pick ? ritualSummon || charged || spellGrant || consume_inventory_id || castLevel !== null
                        ? { variable2: summonPlacement.pick } : { variable1: summonPlacement.pick } : {}),
                };
                const gathered = await ensureAbilityGateData({ casterId: String(caster_id), abilityId: item_id_used, encounterId: encounter_id, ...variables });
                const gate = browserAbilityGateVerdict({ casterId: String(caster_id), encounterId: String(encounter_id), ability, gathered,
                    consumeInventoryId: consume_inventory_id,
                    ...(consumeInventoryUnit ? { consumeInventoryUnit: true as const } : {}), ...variables });
                if (gate.kind === 'refuse') { displayWarning(gate.message); cancelTargetDraft('ability-request-refused'); return; }
                if (gate.kind !== 'allow') legacyRoute = 'gate-' + gate.kind + ':' + gate.site + ('reason' in gate && gate.reason ? ':' + String(gate.reason) : '');
                if (gate.kind === 'allow') {
                    laneDecided = true; // the lane records its own event (served / refused / a named hand-back)
                    let outcome: 'handled' | 'refused' | null = null;
                    try {
                        outcome = await w.ftTryCommitAbilityUse({ encounterId: String(encounter_id), actorId: String(caster_id), targetIds: summonPlacement ? [] : targetIds,
                            ...(placed ? { placed, placementProof: { ...stage, ...activePlacement?.envelope?.intent } } : {}),
                            ...(summonPlacement ? { summon: { ...summonPlacement, ...(ritualSummon ? { ritual: true as const } : {}) } } : {}),
                            // lane A9: the raw selection the legacy request would post (standard_inputs_v2 → status_casting_targets_arr)
                            selection: String(target_id ?? '').replace(/,+$/, ''),
                            abilityId: String(item_id_used), rawDiceMod: Number(raw_dice_mod) || 0,
                            rawDiceAdvDisStacks: Number(raw_dice_adv_dis_stacks) || 0, rollPrivate: Number(roll_private),
                            ...(consume_inventory_id ? { consumeInventoryId: consume_inventory_id,
                                ...(consumeInventoryUnit ? { consumeInventoryUnit: true as const } : {}) } : {}),
                            ...(throwObject ? { throwObject } : {}), ...(charged ? { charged } : {}), ...(castLevel !== null ? { castLevel } : {}),
                            ...(spellGrant ? { spellGrant: { mode: spellGrant, ...(spellGrantSource ? { sourceId: spellGrantSource } : {}) } } : {}),
                            ...(damageTypePick !== null ? { damageTypePick } : {}) });
                    } catch (error) { reportDeliveryThrow(error); console.error('ftTryCommitAbilityUse failed', error); displayWarning(escapeHtml(abilityDeliveryFailureMessage(error))); outcome = 'refused'; }
                    if (outcome !== null && summonPlacement) window.ftSummonPoints = null; // this cast's banked spots are spent
                    if (outcome !== null) { cancelActiveFt3dPlacement(); cancelTargetDraft('ability-request-complete'); return; }
                }
            }
        }
    }
    if (!laneDecided && legacyRoute && encounter_id && caster_id)
        w.ftReportAbilityRoute?.({ encounterId: String(encounter_id), actorId: String(caster_id), abilityId: String(item_id_used) }, legacyRoute);

    cancelActiveFt3dPlacement();

    await queryAbilityData(legacyRoute ?? 'legacy-ability-route-removed', {
        encounter_id,
        caster_id,
        item_id_used,
        ...(charged ? { use_ability_variable_1: charged.mode, use_ability_variable_2: charged.inventoryId } : {}),
        ...(spellGrant ? { use_ability_variable_1: spellGrant, ...(spellGrantSource ? { use_ability_variable_2: spellGrantSource } : {}) } : {}),
        ...(castLevel !== null ? { use_ability_variable_1: castLevel } : {}),
        ...(damageTypePick !== null ? { use_ability_variable_1: damageTypePick } : {}),
    });
    } finally { finishTimingInput?.(); }
}


// Attached attacks use the weapon truth lane with an exact inventory instance.
async function useAbilityWithWeapon(item_id_used: unknown, weapon_inventory_id: string, consume_inventory_id: string | null = null, attackMode: 'throw_weapon' | null = null) {
    const finishTimingInput = beginActionTimingInput();
    try {
    // The fold-out's plain Throw carries an inventory id, not an ability definition.
    // Reuse the ordinary weapon entry point so its exact binder owns the gates.
    if (!consume_inventory_id && String(item_id_used).toUpperCase() === weapon_inventory_id.toUpperCase())
        return useAbility(weapon_inventory_id, null, null, attackMode);
    openSideLog();
    cancelActiveFt3dPlacement();
    w.ftThrowMode = null;
    try {
        const detail = { abilityId: String(item_id_used ?? ''), weaponInventoryId: weapon_inventory_id, consumeInventoryId: consume_inventory_id, attackMode };
        if (consume_inventory_id) { refuseUnported('weapon:family:consume_inventory_id', detail); return; }
        const context = getSidelogContext();
        const ability = abilityBlob(item_id_used);
        if (!context?.encounter_id || !context.caster_id || !ability) { refuseUnported('weapon:page-data-unavailable', detail); return; }
        const { caster_id, encounter_id, campaign_id, target_id: selected_target_id, raw_dice_mod, raw_dice_adv_dis_stacks, roll_private } = context;
        const gathered = await ensureAbilityGateData({ casterId: String(caster_id), abilityId: item_id_used, encounterId: encounter_id });
        const gate = browserAbilityGateVerdict({ casterId: String(caster_id), encounterId: String(encounter_id), ability, gathered,
            weaponInventoryId: weapon_inventory_id, variable1: attackMode ?? undefined });
        if (gate.kind === 'refuse') { displayWarning(gate.message); return; }
        if (gate.kind !== 'allow') { refuseUnported('weapon:gate:' + gate.site, detail); return; }
        if (clientTargetGateBlocksUse(item_id_used, caster_id, selected_target_id, encounter_id)) return;
        const targets = preLaneTargetGates({ item_id_used, caster_id, encounter_id, campaign_id, target_id: selected_target_id,
            use_ability_variable_1: attackMode ?? undefined });
        const gated = targets ? await targets : { refused: false, targetId: selected_target_id };
        if (gated.refused) return;
        const parsed = w.parseTargetIdField?.(String(gated.targetId ?? ''));
        const targetIds = Array.isArray(parsed?.entityIds) ? parsed.entityIds : [];
        if (!parsed || parsed.mapPointToken || targetIds.length !== 1) { refuseUnported('weapon:family:target-selection', detail); return; }
        if (!w.ftTryCommitWeaponAttack) { refuseUnported('weapon:attack-lane-unavailable', detail); return; }
        const result = await w.ftTryCommitWeaponAttack({ encounterId: String(encounter_id), actorId: String(caster_id), targetId: String(targetIds[0]),
            weaponId: weapon_inventory_id, abilityId: String(item_id_used).toUpperCase() === weapon_inventory_id.toUpperCase() ? undefined : String(item_id_used), attackMode,
            rawDiceMod: Number(raw_dice_mod) || 0, rawDiceAdvDisStacks: Number(raw_dice_adv_dis_stacks) || 0, rollPrivate: Number(roll_private) || 0 });
        if (result === null) refuseUnported('weapon:attack-state-unavailable', detail);
    } catch (error) {
        console.error('Explicit weapon attack failed', error);
        refuseUnported('weapon:attack-input-unavailable', { abilityId: String(item_id_used), weaponInventoryId: weapon_inventory_id });
    } finally {
        cancelTargetDraft('weapon-request-complete');
    }
    } finally { finishTimingInput?.(); }
}


/** Layer readers for casterStatusChoiceIds (the page's ftLayer* globals) — lane PC1's status-choice route. */
const statusChoiceLaneReaders = {
    has: (row: Record<string, unknown> | null, key: string): boolean => !!row && ftLayerValue(row, key) != null,
    entries: (row: Record<string, unknown> | null, key: string): unknown[] =>
        (row ? (window as unknown as { ftLayerEntries?: (r: unknown, k: string) => unknown[] }).ftLayerEntries?.(row, key) : []) ?? [],
    value: (row: Record<string, unknown> | null, key: string): unknown => (row ? ftLayerValue(row, key) : null),
};

/** Lane PC1 (Round 36): item.php's PC sheet seeds only a LEAN encounter (no roster), so the ability lane skips the cast
 *  (`missing-page-data`) and the cast is refused. Warm the full encounter blob for browser-only layers first
 *  (getEncounterArr: cache-first, fetches only a lean / missing
 *  one and fills encounterCache). Every other cast keeps its route unchanged. */
async function ensureLaneEncounterForBrowserOnly(encounterId: unknown, abilityId: unknown, casterId: unknown, required = false): Promise<void> {
    const encId = String(encounterId ?? '');
    if (!encId || typeof getEncounterArr !== 'function') return;
    const ability = abilityBlob(abilityId) as Record<string, unknown> | null;
    const caster = ((window.FateTide as { casterCache?: Map<string, unknown> } | undefined)?.casterCache?.get(String(casterId ?? '').toUpperCase()) ?? null) as Record<string, unknown> | null;
    if (!required && !browserOnlyLegacyRouteReason([ability, caster], ftLayerValue, 'probe')) return;
    const cached = (window.FateTide as { encounterCache?: Map<string, unknown> } | undefined)?.encounterCache?.get(encId) as Record<string, unknown> | undefined;
    if (cached && cached.encounter_creatures_arr !== undefined) return;
    try { await getEncounterArr(encId); } catch (error) { console.error('ensureLaneEncounterForBrowserOnly failed', error); }
}

/** Lane PC1 (R-151): a status_effect_applied_is_caster_choice ability fired with NO pick (an empty select) is an ordinary cast — PHP's and
 *  the engine's default is the first option — so it takes useAbility()'s lane instead of a legacy post carrying an empty variable. */
function statusChoiceWithoutPick(item_id_used: unknown, variable1: unknown): boolean {
    const ability = abilityBlob(item_id_used) as Record<string, unknown> | null;
    return !!ability && (variable1 == null || String(variable1).trim() === '') && casterStatusChoiceIds(statusChoiceLaneReaders, ability).length > 0;
}

/** The committed FT3D AREA placement this dispatch casts (placementKind 'aoe', for this ability). Read synchronously: FT3DAOEPlacement.cast()
 *  cancels the placement as soon as the dispatch first awaits. */
function committedAreaPlacementFor(abilityId: unknown): boolean {
    const active = window.aoeManager?.ft3dActive as { committed?: boolean; envelope?: { intent?: { placementKind?: string; abilityId?: string } | null } } | null | undefined;
    const intent = active?.committed ? active.envelope?.intent : null;
    return !!intent && intent.placementKind === 'aoe' && String(intent.abilityId ?? '').toUpperCase() === String(abilityId ?? '').toUpperCase();
}

/** The ability's own damage types (upper-case ids) — what its user_choice_1_damage_type select offers. */
function ownDamageTypes(ability: Record<string, unknown> | null): string[] {
    if (!ability) return [];
    const entries = (window as unknown as { ftLayerEntries?: (r: unknown, k: string) => unknown[] }).ftLayerEntries?.(ability, 'damage_type') ?? [];
    return entries.map(entry => String((typeof entry === 'string' ? entry
        : (entry as { items?: { item_id?: unknown }; item_id?: unknown })?.items?.item_id ?? (entry as { item_id?: unknown })?.item_id) ?? '').trim().toUpperCase())
        .filter(Boolean);
}

/** Damage chips share the cast's browser commit/sink, without entering the cast's gates or resources. */
async function useAbilityDamageOnly(itemId: unknown, damageType?: unknown): Promise<void> {
    openSideLog();
    const context = getSidelogContext();
    const actorId = String(context?.caster_id ?? '');
    const abilityId = String(itemId ?? '');
    const parsed = w.parseTargetIdField?.(String(context?.target_id ?? ''));
    const targetIds = parsed?.mapPointToken || !Array.isArray(parsed?.entityIds) ? [] : parsed.entityIds.map(String).filter(Boolean);
    try {
        if (!actorId || !context?.encounter_id || !w.ftTryCommitAbilityUse) {
            refuseUnported('damage-only:context-unavailable', { actorId, abilityId }, 'Open this character in an encounter to apply damage.');
            return;
        }
        if (!targetIds.length) {
            refuseUnported('damage-only:target-required', { actorId, abilityId }, 'Choose a target for this damage roll.');
            return;
        }
        const outcome = await w.ftTryCommitAbilityUse({ encounterId: String(context.encounter_id), actorId, abilityId, targetIds,
            damageOnly: true, ...(damageType ? { damageTypePick: String(damageType) } : {}),
            rollPrivate: Number(context.roll_private) || 0, rawDiceMod: Number(context.raw_dice_mod) || 0,
            rawDiceAdvDisStacks: Number(context.raw_dice_adv_dis_stacks) || 0 });
        if (outcome === null) refuseUnported('damage-only:lane-unavailable', { actorId, abilityId });
    } catch (error) {
        refuseUnported('damage-only:delivery-failed', { actorId, abilityId, error: String(error) }, 'Damage delivery failed. Try again.');
    } finally { cancelTargetDraft('damage-only-complete'); }
}

async function useAbilityVariable1(item_id_used: unknown, use_ability_variable_1: unknown, consume_inventory_id: string | null = null) {
    if (use_ability_variable_1 === 'damage_only') return useAbilityDamageOnly(item_id_used);
    if (!consume_inventory_id && /^\d{1,2}$/.test(String(use_ability_variable_1))
        && w.ftArmInstantProjectileSpell?.(String(getSidelogContext()?.caster_id ?? ''), String(item_id_used ?? ''),
            String(use_ability_variable_1))) return;
    const finishTimingInput = beginActionTimingInput();
    try {
    // The object card passes an inventory identity. Resolve its template before the ordinary cast gates;
    // this explicit Consume action spends one unit even when the template has no unit-use flag.
    if (use_ability_variable_1 === 'consume') {
        const sourceId = String(item_id_used ?? '').toUpperCase();
        const source = (w as Window & { ftResolveCachedInventoryRow?: (id: string) => Record<string, unknown> | null })
            .ftResolveCachedInventoryRow?.(sourceId) ?? abilityBlob(sourceId)
            ?? pageInventoryRow(sourceId, String(getSidelogContext()?.encounter_id ?? ''));
        const templateId = String(ftInventoryField(source, 'inventory_item_id') ?? '').toUpperCase();
        if (!source || !CHARGED_SOURCE_UUID.test(templateId)) {
            refuseUnported('consume:inventory-source-not-held'); cancelTargetDraft('ability-request-refused'); return;
        }
        if (!abilityBlob(templateId)) {
            const fetchObject = (w as Window & { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
            if (fetchObject) await fetchObject(templateId);
        }
        if (!abilityBlob(templateId)) {
            refuseUnported('consume:definition-not-held:' + templateId); cancelTargetDraft('ability-request-refused'); return;
        }
        return useAbility(templateId, sourceId, null, null, null, null, null, undefined, null, true);
    }
    if (use_ability_variable_1 === 'throw_weapon' && !consume_inventory_id) return useAbility(item_id_used, null, null, 'throw_weapon');
    if (isSpellGrantMode(use_ability_variable_1) && !consume_inventory_id)
        return useAbility(item_id_used, null, null, null, null, null, use_ability_variable_1);
    if (statusChoiceWithoutPick(item_id_used, use_ability_variable_1)) return useAbility(item_id_used, consume_inventory_id);
    const amountAbility = typeof use_ability_variable_1 === 'string' && use_ability_variable_1.trim() !== ''
        && Number.isFinite(Number(use_ability_variable_1)) ? abilityBlob(item_id_used) : null;
    if (usesGroundSummonPlacement(amountAbility))
        return useAbility(item_id_used, consume_inventory_id, null, null, String(use_ability_variable_1));
    if (amountAbility && isVariableAmount({ read: { value: ftLayerValue } }, amountAbility, use_ability_variable_1))
        return useAbility(item_id_used, consume_inventory_id, null, null, String(use_ability_variable_1));
    // A DAMAGE-TYPE pick (user_choice_1_damage_type — Elemental Grasp, playtest 2026-10-13): one of the ability's own damage types rides
    // useAbility's lane (creature or placed selection alike) as damageTypePick; the engine applies it to the cast's own damage fragment.
    if (typeof use_ability_variable_1 === 'string'
        && ownDamageTypes(abilityBlob(item_id_used)).includes(use_ability_variable_1.trim().toUpperCase()))
        return useAbility(item_id_used, consume_inventory_id, null, null, null, use_ability_variable_1.trim().toUpperCase());
    // A PLACED area cast at a level (playtest 2026-10-13, Fog Cloud): useAbility owns the placed-area lane — hand it the level before any await.
    if (typeof use_ability_variable_1 === 'string' && /^\d{1,2}$/.test(use_ability_variable_1) && committedAreaPlacementFor(item_id_used))
        return useAbility(item_id_used, consume_inventory_id, null, null, use_ability_variable_1);

    // Keep the log visible for a remaining inventory-backed throw variant.
    if (use_ability_variable_1 === 'throw_weapon') {
        openSideLog();
    }

    // Preserve the confirmed pre-cast draft until the browser lane or refusal settles this cast.

    const quick_ritual_fields = consumeQuickRitualFlag();

    const sl_context = getSidelogContext();
    if (!sl_context) {
        refuseUnported('ability-use:context-unavailable', undefined, 'Reopen this character\'s sheet before using an ability.');
        return;
    }

    const {
        roll_private,
        campaign_id,
        encounter_id,
        caster_id,
        target_id: selected_target_id,
        raw_dice_mod,
        raw_dice_adv_dis_stacks,
    } = sl_context;
    let target_id = selected_target_id;

    const shapeshiftAbility = abilityBlob(item_id_used) as Record<string, unknown> | null;
    const shapeshiftPick = !!shapeshiftAbility && typeof use_ability_variable_1 === 'string' && CHARGED_SOURCE_UUID.test(use_ability_variable_1)
        && SHAPESHIFT_LAYERS.some(key => ftLayerValue(shapeshiftAbility, key) != null);
    // Self must replace an unrelated draft before the shared selection gates judge it.
    if (shapeshiftPick && caster_id && selfRangeCast(shapeshiftAbility, caster_id) && !ownItemPicker(shapeshiftAbility)
        && ftLayerValue(shapeshiftAbility, 'aoe_type') == null) target_id = String(caster_id);

    // Validate required fields
    const rules = {
        roll_private: { type: 'enum', value: roll_private, allowed: ['0', '1', '2'] },
        caster_id: { type: 'uuid', value: caster_id, optional: true },
        raw_dice_mod: { type: 'number', value: raw_dice_mod },
        raw_dice_adv_dis_stacks: { type: 'number', value: raw_dice_adv_dis_stacks },
        item_id_used: { type: 'uuid', value: item_id_used },
        use_ability_variable_1: { type: 'string', value: use_ability_variable_1 },
    };
    const validationResult = validateContext(rules, 'useAbilityVariable1');
    if (!validationResult.valid) {
        refuseUnported('ability-use:invalid-context', { error: validationResult.error });
        return;
    }

    if (use_ability_variable_1 === 'arm_counterspell' && !consume_inventory_id && encounter_id && caster_id
        && typeof w.ftTryCommitAbilityUse === 'function'
        && (w as unknown as { ftAbilityLaneFeatures?: { armCounterspell?: boolean } }).ftAbilityLaneFeatures?.armCounterspell) {
        const parsed = w.parseTargetIdField?.(String(target_id ?? ''));
        const targetIds = !parsed?.mapPointToken && Array.isArray(parsed?.entityIds) ? parsed.entityIds.map(String).filter(Boolean) : [];
        openSideLog();
        try {
            await ensureLaneEncounterForBrowserOnly(encounter_id, item_id_used, caster_id);
            // This is the existing action-owned confirmed draft. RX will judge the eventual spell independently.
            const outcome = await w.ftTryCommitAbilityUse({ encounterId: String(encounter_id), actorId: String(caster_id), targetIds,
                abilityId: String(item_id_used), rollPrivate: Number(roll_private), armCounterspell: true });
            if (outcome !== null) return;
        } catch (error) {
            reportDeliveryThrow(error);
            console.error('ftTryCommitAbilityUse (counterspell ready) failed', error);
            displayWarning(escapeHtml(abilityDeliveryFailureMessage(error)));
            return;
        } finally { cancelTargetDraft('ability-request-complete'); }
    }

    // END CONCENTRATION (FT3D playtest 2026-10-13 — it was refused `legacy-ability-route-removed`): the info panel's button names the caster's
    // Concentrating row on variable_1. It always acts on its caster, so it rides the lane ahead of the selection gates; the engine judges the
    // row (registry-dispatch-rolls.ts endConcentrationRoll) and the commit's concentration tail ends the whole connection.
    if (String(item_id_used).toUpperCase() === END_CONCENTRATION_ABILITY && typeof use_ability_variable_1 === 'string'
        && CHARGED_SOURCE_UUID.test(use_ability_variable_1)
        && typeof w.ftTryCommitAbilityUse === 'function' && encounter_id && caster_id && !consume_inventory_id) {
        // A global ability, never in a caster's own catalog: read its definition into the object cache first (craftStartLane's pattern).
        let ability = abilityBlob(END_CONCENTRATION_ABILITY);
        const fetchObject = (w as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
        if (!ability && typeof fetchObject === 'function') {
            try { await fetchObject(END_CONCENTRATION_ABILITY); } catch { /* unheld → the named refusal below */ }
            ability = abilityBlob(END_CONCENTRATION_ABILITY);
        }
        if (ability) {
            const gathered = await ensureAbilityGateData({ casterId: String(caster_id), abilityId: END_CONCENTRATION_ABILITY, encounterId: encounter_id });
            const gate = browserAbilityGateVerdict({ casterId: String(caster_id), encounterId: String(encounter_id), ability, gathered });
            if (gate.kind === 'refuse') { displayWarning(gate.message); cancelTargetDraft('ability-request-refused'); return; }
            if (gate.kind === 'allow') {
                let outcome: 'handled' | 'refused' | null = null;
                try {
                    outcome = await w.ftTryCommitAbilityUse({ encounterId: String(encounter_id), actorId: String(caster_id), targetIds: [String(caster_id)],
                        abilityId: END_CONCENTRATION_ABILITY, rollPrivate: Number(roll_private), endConcentration: use_ability_variable_1.toUpperCase() });
                } catch (error) { reportDeliveryThrow(error); console.error('ftTryCommitAbilityUse (end concentration) failed', error); displayWarning(escapeHtml(abilityDeliveryFailureMessage(error))); outcome = 'refused'; }
                if (outcome !== null) { cancelTargetDraft('ability-request-complete'); return; }
            } else {
                w.ftReportAbilityRoute?.({ encounterId: String(encounter_id), actorId: String(caster_id), abilityId: END_CONCENTRATION_ABILITY },
                    'end-concentration:gate-' + String(gate.kind) + ('reason' in gate && gate.reason ? ':' + String(gate.reason) : ''));
            }
        } else {
            w.ftReportAbilityRoute?.({ encounterId: String(encounter_id), actorId: String(caster_id), abilityId: END_CONCENTRATION_ABILITY },
                'end-concentration:definition-not-held');
        }
    }

    // Wizard v9 WP3: the committed FT3D summon placement, read before the first await (FT3DAOEPlacement.commit cancels it once this awaits)
    const summonPlacementAtDispatch = window.aoeManager?.ft3dActive ?? null;
    if (clientTargetGateBlocksUse(item_id_used, caster_id, target_id, encounter_id)) return;
    // The cast-time TARGET gates (R-103) ahead of every client-truth lane; the unseen picks they drop leave this dispatch's selection.
    const targetGates = preLaneTargetGates({ item_id_used, caster_id, target_id, encounter_id, campaign_id, use_ability_variable_1: use_ability_variable_1 });
    if (targetGates) { const gated = await targetGates; if (gated.refused) return; target_id = gated.targetId as typeof target_id; }
    // Refuse a picked hurl before the level-cast mutation lane.
    try {
        w.ftHurlBind?.({ casterId: String(caster_id ?? ''), abilityId: String(item_id_used), encounterId: String(encounter_id ?? ''),
            targetSelection: String(target_id ?? '') });
    } catch (error) {
        if ((error as { name?: string })?.name === 'HurlBindingRefused') return;
        throw error;
    }

    // Wizard v9 WP3 (2026-10-09): a user_choice_1_summon PICK confirmed on an FT3D summon placement rides the summon lane
    // (janus-engine/ability/summon.ts) — judged by the page gate WITH its pick, then decided and committed in the browser.
    const pickPlacement = typeof use_ability_variable_1 === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(use_ability_variable_1)
        ? summonPlacementOf(summonPlacementAtDispatch, caster_id, item_id_used, encounter_id, target_id) : null;
    if (pickPlacement && typeof w.ftTryCommitAbilityUse === 'function' && encounter_id && caster_id && !consume_inventory_id
        && Object.keys(quick_ritual_fields).length === 0) {
        const ability = abilityBlob(item_id_used);
        if (ability) {
            const readiness = ensureSummonPickDefinition(ability, String(use_ability_variable_1));
            if (!(typeof readiness === 'boolean' ? readiness : await readiness)) {
                window.ftSummonPoints = null; cancelActiveFt3dPlacement(); cancelTargetDraft('ability-request-refused'); return;
            }
            const gateRequest = { casterId: String(caster_id), encounterId: String(encounter_id), ability, variable1: use_ability_variable_1 };
            let gate = browserAbilityGateVerdict(gateRequest);
            if (gate.kind === 'unknown') {
                const gathered = await ensureAbilityGateData({ ...gateRequest, abilityId: item_id_used });
                gate = browserAbilityGateVerdict({ ...gateRequest, gathered });
            }
            if (gate.kind === 'refuse') { displayWarning(gate.message); cancelTargetDraft('ability-request-refused'); return; }
            if (gate.kind === 'allow') {
                let outcome: 'handled' | 'refused' | null = null;
                try {
                    outcome = await w.ftTryCommitAbilityUse({ encounterId: String(encounter_id), actorId: String(caster_id), targetIds: [],
                        selection: String(target_id ?? '').replace(/,+$/, ''), abilityId: String(item_id_used), rawDiceMod: Number(raw_dice_mod) || 0,
                        rawDiceAdvDisStacks: Number(raw_dice_adv_dis_stacks) || 0, rollPrivate: Number(roll_private),
                        summon: { ...pickPlacement, pick: String(use_ability_variable_1) } });
                } catch (error) { reportDeliveryThrow(error); console.error('ftTryCommitAbilityUse (summon pick) failed', error); displayWarning(escapeHtml(abilityDeliveryFailureMessage(error))); outcome = 'refused'; }
                if (outcome !== null) { window.ftSummonPoints = null; cancelActiveFt3dPlacement(); cancelTargetDraft('ability-request-complete'); return; }
            }
        }
    }

    // Wildshift's creature UUID is a form pick, judged by the page WITH variable_1 and kept intact through the generic lane.
    if (shapeshiftPick && shapeshiftAbility && typeof use_ability_variable_1 === 'string') {
        const form = use_ability_variable_1.trim().toUpperCase();
        const rejectPick = (reason: string): void => { refuseUnported(reason, { abilityId: String(item_id_used) }); cancelTargetDraft('ability-request-refused'); };
        if (!isShapeshiftPickVariable({ read: { ...statusChoiceLaneReaders, definition: id => abilityBlob(id) as Record<string, unknown> | null } },
            shapeshiftAbility, form, null)) { displayWarning('This form is not offered by this ability or spell!'); cancelTargetDraft('ability-request-refused'); return; }
        if ((w as unknown as { ftAbilityLaneFeatures?: { shapeshiftPick?: boolean } }).ftAbilityLaneFeatures?.shapeshiftPick !== true
            || typeof w.ftTryCommitAbilityUse !== 'function') { rejectPick('shapeshift:pick-lane-unavailable'); return; }
        if (!encounter_id || !caster_id || consume_inventory_id || Object.keys(quick_ritual_fields).length || window.aoeManager?.ft3dActive) {
            rejectPick('shapeshift:cast-context'); return;
        }
        let definition = abilityBlob(form) as Record<string, unknown> | null;
        const fetchObject = (w as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
        if (!definition?.item_layer_types && typeof fetchObject === 'function') {
            try { await fetchObject(form); } catch { /* the named refusal below retires the request */ }
            definition = abilityBlob(form) as Record<string, unknown> | null;
        }
        if (!definition?.item_layer_types) { rejectPick('shapeshift:definition-not-held:' + form); return; }
        const parsed = w.parseTargetIdField?.(String(target_id ?? '')) ?? { mapPointToken: null, entityIds: [] };
        let targets = Array.isArray(parsed.entityIds) ? parsed.entityIds.map(v => String(v ?? '').trim()).filter(Boolean) : [];
        if (!parsed.mapPointToken && selfRangeCast(shapeshiftAbility, caster_id) && !ownItemPicker(shapeshiftAbility)
            && (!targets.length || ftLayerValue(shapeshiftAbility, 'aoe_type') == null)) targets = [String(caster_id)];
        if (!targets.length || parsed.mapPointToken) { rejectPick('shapeshift:creature-target-required'); return; }
        await ensureLaneEncounterForBrowserOnly(encounter_id, item_id_used, caster_id, true);
        const gathered = await ensureAbilityGateData({ casterId: String(caster_id), abilityId: item_id_used, encounterId: encounter_id, variable1: form });
        const gate = browserAbilityGateVerdict({ casterId: String(caster_id), encounterId: String(encounter_id), ability: shapeshiftAbility, gathered, variable1: form });
        if (gate.kind === 'refuse') { displayWarning(gate.message); cancelTargetDraft('ability-request-refused'); return; }
        if (gate.kind !== 'allow') { rejectPick('shapeshift:gate-' + gate.kind + ('reason' in gate ? ':' + String(gate.reason) : '')); return; }
        openSideLog();
        try {
            const outcome = await w.ftTryCommitAbilityUse({ encounterId: String(encounter_id), actorId: String(caster_id), targetIds: targets,
                selection: String(target_id ?? '').replace(/,+$/, ''), abilityId: String(item_id_used), rawDiceMod: Number(raw_dice_mod) || 0,
                rawDiceAdvDisStacks: Number(raw_dice_adv_dis_stacks) || 0, rollPrivate: Number(roll_private), shapeshiftForm: form });
            if (outcome === 'handled' || outcome === 'refused') return;
            rejectPick('shapeshift:delivery-unavailable');
        } catch (error) { reportDeliveryThrow(error); console.error('ftTryCommitAbilityUse (shapeshift) failed', error); displayWarning(escapeHtml(abilityDeliveryFailureMessage(error))); }
        finally { cancelTargetDraft('ability-request-complete'); }
        return;
    }

    // Client-truth STATUS-CHOICE PICK (Janus lane PC1, 2026-10-09 — R-151): an ability carrying status_effect_applied_is_caster_choice
    // (Override Emotion: Charmed or Frightened) posts its pick on use_ability_variable_1, which no lane branch took — every picked cast went
    // to the refusal chokepoint. The pick rides the generic lane (janus.ts resolveCasterStatusChoice narrows the appliers and consumes it). Only
    // when the lane bundle this page talks to reads the pick (ftAbilityLaneFeatures — a bundle skew must never resolve the default instead).
    const choiceAbility = abilityBlob(item_id_used) as Record<string, unknown> | null;
    const laneReadsStatusChoice = (w as unknown as { ftAbilityLaneFeatures?: { statusChoice?: boolean } }).ftAbilityLaneFeatures?.statusChoice === true;
    if (laneReadsStatusChoice && typeof w.ftTryCommitAbilityUse === 'function' && encounter_id && caster_id && !consume_inventory_id
        && Object.keys(quick_ritual_fields).length === 0 && !window.aoeManager?.ft3dActive && choiceAbility
        && typeof use_ability_variable_1 === 'string'
        && casterStatusChoiceIds(statusChoiceLaneReaders, choiceAbility).includes(use_ability_variable_1.trim().toUpperCase())) {
        const parsedTargets = w.parseTargetIdField?.(String(target_id ?? '')) ?? { mapPointToken: null, entityIds: [] };
        let targetIds = Array.isArray(parsedTargets.entityIds) ? (parsedTargets.entityIds as unknown[]).map(v => String(v ?? '').trim()).filter(Boolean) : [];
        // a Self-range cast is on its caster whatever is selected unless it has an area or is an own-item picker (useAbility's rule)
        if (!parsedTargets.mapPointToken && selfRangeCast(choiceAbility, caster_id) && !ownItemPicker(choiceAbility)
            && (!targetIds.length || ftLayerValue(choiceAbility, 'aoe_type') == null)) targetIds = [String(caster_id)];
        if (targetIds.length > 0 && !parsedTargets.mapPointToken) {
            await ensureLaneEncounterForBrowserOnly(encounter_id, item_id_used, caster_id);
            const gathered = await ensureAbilityGateData({ casterId: String(caster_id), abilityId: item_id_used, encounterId: encounter_id, variable1: use_ability_variable_1 });
            const gate = browserAbilityGateVerdict({ casterId: String(caster_id), encounterId: String(encounter_id), ability: choiceAbility, gathered, variable1: use_ability_variable_1 });
            if (gate.kind === 'refuse') { displayWarning(gate.message); cancelTargetDraft('ability-request-refused'); return; }
            if (gate.kind === 'allow') {
                let outcome: 'handled' | 'refused' | null = null;
                try {
                    outcome = await w.ftTryCommitAbilityUse({ encounterId: String(encounter_id), actorId: String(caster_id), targetIds,
                        selection: String(target_id ?? '').replace(/,+$/, ''), abilityId: String(item_id_used), rawDiceMod: Number(raw_dice_mod) || 0,
                        rawDiceAdvDisStacks: Number(raw_dice_adv_dis_stacks) || 0, rollPrivate: Number(roll_private),
                        statusChoice: use_ability_variable_1.trim().toUpperCase() });
                } catch (error) { reportDeliveryThrow(error); console.error('ftTryCommitAbilityUse (status choice) failed', error); displayWarning(escapeHtml(abilityDeliveryFailureMessage(error))); outcome = 'refused'; }
                if (outcome !== null) { cancelActiveFt3dPlacement(); cancelTargetDraft('ability-request-complete'); return; }
            } else if (encounter_id && caster_id) {
                w.ftReportAbilityRoute?.({ encounterId: String(encounter_id), actorId: String(caster_id), abilityId: String(item_id_used) },
                    'status-choice:gate-' + String(gate.kind) + ('reason' in gate && gate.reason ? ':' + String(gate.reason) : ''));
            }
        }
    }

    // Client-truth LEVEL CAST (Janus Session 1 step 5a, 2026-09-29): the sheet's "cast at level N" picker on a leveled spell / curse /
    // menta at a creature selection rides the generic ability lane (janus-engine/ability/upcast.ts); a placed area, a consumable source
    // or any non-numeric variable reaches the refusal below when no supported lane handles it.
    if (typeof w.ftTryCommitAbilityUse === 'function' && encounter_id && caster_id && !consume_inventory_id
        && Object.keys(quick_ritual_fields).length === 0 && !window.aoeManager?.ft3dActive
        && typeof use_ability_variable_1 === 'string' && /^\d{1,2}$/.test(use_ability_variable_1)) {
        const ability = abilityBlob(item_id_used);
        const level = Number(ftLayerValue(ability, 'spell_level') ?? ftLayerValue(ability, 'menta_level') ?? 0);
        const parsedTargets = w.parseTargetIdField?.(String(target_id ?? '')) ?? { mapPointToken: null, entityIds: [] };
        let targetIds = Array.isArray(parsedTargets.entityIds) ? (parsedTargets.entityIds as unknown[]).map(v => String(v ?? '').trim()).filter(Boolean) : [];
        // a Self-range cast is on its caster (validate_and_adjust_targets L1690) — lane A5, as useAbility above; and, like the A7 rule there,
        // WHATEVER is selected unless it has an area or is an own-item picker (coordinator Codex A7 review P1: a leveled Self cast kept an
        // unrelated selected creature as its target — PHP forces the caster, use_ability L1693–1703)
        // …and a Self follow AURA is the caster whatever is selected (the first site's rule — Codex A8 r3 P2)
        const levelAura = !!ability && String(ftLayerValue(ability, 'range') ?? '').toUpperCase() === 'D82855E1-4BB9-93EA-6374-B9904318BC1D'
            && ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(key => ftLayerValue(ability, key) != null);
        if (!parsedTargets.mapPointToken && (levelAura || (selfRangeCast(ability, caster_id) && !ownItemPicker(ability)
            && (!targetIds.length || ftLayerValue(ability, 'aoe_type') == null)))) targetIds = [String(caster_id)];
        if (ability && ['spell', 'curse', 'menta'].includes(String(ftItemsCol(ability, 'item_type') ?? '').toLowerCase()) && level > 0
            && targetIds.length > 0 && !parsedTargets.mapPointToken) {
            const gathered = await ensureAbilityGateData({ casterId: String(caster_id), abilityId: item_id_used, encounterId: encounter_id });
            const gate = browserAbilityGateVerdict({ casterId: String(caster_id), encounterId: String(encounter_id), ability, gathered });
            if (gate.kind === 'refuse') { displayWarning(gate.message); cancelTargetDraft('ability-request-refused'); return; }
            if (gate.kind === 'allow') {
                let outcome: 'handled' | 'refused' | null = null;
                try {
                    outcome = await w.ftTryCommitAbilityUse({ encounterId: String(encounter_id), actorId: String(caster_id), targetIds,
                        abilityId: String(item_id_used), rawDiceMod: Number(raw_dice_mod) || 0, rawDiceAdvDisStacks: Number(raw_dice_adv_dis_stacks) || 0,
                        rollPrivate: Number(roll_private), castLevel: use_ability_variable_1 });
                } catch (error) { reportDeliveryThrow(error); console.error('ftTryCommitAbilityUse (level cast) failed', error); displayWarning(escapeHtml(abilityDeliveryFailureMessage(error))); outcome = 'refused'; }
                if (outcome !== null) { cancelActiveFt3dPlacement(); cancelTargetDraft('ability-request-complete'); return; }
            }
        }
    }

    cancelActiveFt3dPlacement();
    await queryAbilityData('legacy-ability-route-removed', {
        encounter_id,
        caster_id,
        item_id_used,
        use_ability_variable_1,
    });
    } finally { finishTimingInput?.(); }
}


// Ritual cast + user_choice_1_summon (e.g. Summon Familiar): spell_ritual must be sent with the creature UUID in use_ability_variable_2
// so the server can persist it on Casting Ritual Spell / Concentrating and resolve the summon when time elapses.
async function useAbilityRitualSpellWithUserChoice1Summon(item_id_used: unknown, user_choice_1_summon_input_id: unknown) {
    const summonInputId = String(user_choice_1_summon_input_id ?? '');
    const summonId =
        (summonInputId && (document.getElementById(summonInputId) as HTMLInputElement | null)?.value?.trim()) || '';
    if (!summonId) {
        const warnEl = document.getElementById('div_sidelog_warning');
        if (warnEl) {
            warnEl.innerHTML =
                '<div class="alert alert-warning small mb-0">Select a creature in the Effect column before casting this ritual.</div>';
        }
        ftDebugLog('ability', { warn: 'useAbilityRitualSpellWithUserChoice1Summon: no summon selected' });
        return;
    }
    if (!checkIfValidUUID(summonId)) {
        console.error('useAbilityRitualSpellWithUserChoice1Summon: invalid creature UUID', summonId);
        return;
    }
    await useAbilityVariable2(item_id_used, 'spell_ritual', summonId);
}


// for charged spells, abilities when we have a inventory source
// use_ability_variable_1 = charged_spell, charged_ability
// use_ability_variable_2 = inventory_id
async function useAbilityVariable2(item_id_used: unknown, use_ability_variable_1: unknown, use_ability_variable_2: unknown, consume_inventory_id: string | null = null) {
    if (use_ability_variable_1 === 'damage_only') return useAbilityDamageOnly(item_id_used, use_ability_variable_2);
    const finishTimingInput = beginActionTimingInput();
    try {
    // A configured level + creature uses the same ground-summon lane as the plain and charged casts.
    const summonAbility = abilityBlob(item_id_used);
    if (use_ability_variable_1 === 'spell_ritual' && typeof use_ability_variable_2 === 'string'
        && CHARGED_SOURCE_UUID.test(use_ability_variable_2) && usesGroundSummonPlacement(summonAbility)
        && ftLayerValue(summonAbility, 'spell_ritual') != null)
        return useAbility(item_id_used, consume_inventory_id, null, null, null, null, null, undefined,
            null, false, use_ability_variable_2.toUpperCase(), true);
    if (typeof use_ability_variable_2 === 'string' && CHARGED_SOURCE_UUID.test(use_ability_variable_2)
        && (isSpellGrantMode(use_ability_variable_1) || /^\d{1,2}$/.test(String(use_ability_variable_1)))
        && summonAbility && usesGroundSummonPlacement(summonAbility) && ftLayerValue(summonAbility, 'user_choice_1_summon') != null
        && (ftLayerEntries(summonAbility, 'user_choice_1_summon') ?? [])
            .some(entry => readLayerListEntryUuid(entry).toUpperCase() === use_ability_variable_2.toUpperCase())) {
        return useAbility(item_id_used, consume_inventory_id, null, null,
            isSpellGrantMode(use_ability_variable_1) ? null : String(use_ability_variable_1), null,
            isSpellGrantMode(use_ability_variable_1) ? use_ability_variable_1 : null,
            undefined, null, false, use_ability_variable_2.toUpperCase());
    }
    if (use_ability_variable_1 === 'nested_inventory_source' && typeof use_ability_variable_2 === 'string'
        && CHARGED_SOURCE_UUID.test(use_ability_variable_2))
        return useAbility(item_id_used, use_ability_variable_2.toUpperCase());
    if (use_ability_variable_1 === 'throw_inventory' && typeof use_ability_variable_2 === 'string'
        && CHARGED_SOURCE_UUID.test(use_ability_variable_2) && !consume_inventory_id) {
        if ((w as unknown as { ftAbilityLaneFeatures?: { throwObject?: boolean } }).ftAbilityLaneFeatures?.throwObject !== true) {
            w.ftThrowMode = null;
            refuseUnported(abilityVariableModeReason(use_ability_variable_1, use_ability_variable_2));
            cancelTargetDraft('ability-request-refused');
            return;
        }
        return useAbility(item_id_used, null, null, null, null, null, null, undefined,
            { inventoryId: use_ability_variable_2.toUpperCase(), lob: w.ftThrowMode !== 'direct' });
    }

    const amountAbility = typeof use_ability_variable_1 === 'string' && use_ability_variable_1.trim() !== ''
        && Number.isFinite(Number(use_ability_variable_1)) ? abilityBlob(item_id_used) : null;
    if (amountAbility && isVariableAmount({ read: { value: ftLayerValue } }, amountAbility, use_ability_variable_1)
        && typeof use_ability_variable_2 === 'string'
        && (use_ability_variable_2 === '' || ownDamageTypes(amountAbility).includes(use_ability_variable_2.trim().toUpperCase())))
        return useAbility(item_id_used, consume_inventory_id, null, null, String(use_ability_variable_1), use_ability_variable_2.trim().toUpperCase() || null);
    if (isSpellGrantMode(use_ability_variable_1) && !consume_inventory_id
        && (use_ability_variable_2 == null || use_ability_variable_2 === '' || typeof use_ability_variable_2 === 'string' && CHARGED_SOURCE_UUID.test(use_ability_variable_2)))
        return useAbility(item_id_used, null, null, null, null, null, use_ability_variable_1,
            typeof use_ability_variable_2 === 'string' && use_ability_variable_2 ? use_ability_variable_2.toUpperCase() : undefined);

    // A CHARGED-ITEM cast (Wizard v9 WP5) goes through useAbility()'s generic browser lane, which pays the item's charges.
    // Unsupported source or variable contracts refuse.
    const charged = consume_inventory_id ? null : chargedCastOf(use_ability_variable_1, use_ability_variable_2);
    if (charged) {
        await useAbility(item_id_used, null, charged);
        const sidelogObject = document.getElementById('input_hidden_sidelog_object_item_id') as HTMLInputElement | null;
        if (sidelogObject) await openItemInfo(sidelogObject.value, false, __ftShouldQuietRefreshSideInfoPanel());
        return;
    }

    // A Sanguine retype shares the spell's level/pick channel; Janus judges the armed carrier and allowed damage types.
    const retype = typeof use_ability_variable_2 === 'string' ? use_ability_variable_2.trim().toUpperCase() : '';
    if (!consume_inventory_id && ['spell', 'curse'].includes(String(ftItemsCol(abilityBlob(item_id_used), 'item_type') ?? ''))
        && (use_ability_variable_1 == null || use_ability_variable_1 === '' || /^\d{1,2}$/.test(String(use_ability_variable_1)))
        && Object.values((window as Window & { DAMAGETYPE_CATALOG?: Record<string, { item_id?: string }> }).DAMAGETYPE_CATALOG ?? {})
            .some(type => String(type.item_id ?? '').toUpperCase() === retype))
        return useAbility(item_id_used, null, null, null,
            use_ability_variable_1 == null || use_ability_variable_1 === '' ? null : String(use_ability_variable_1), retype);

    const quick_ritual_fields = consumeQuickRitualFlag();

    // Preserve the confirmed pre-cast draft until the browser lane or refusal settles this cast.

    const sl_context = getSidelogContext();
    if (!sl_context) {
        refuseUnported('ability-use:context-unavailable', undefined, 'Reopen this character\'s sheet before using an ability.');
        return;
    }

    const {
        roll_private,
        campaign_id,
        encounter_id,
        caster_id,
        target_id: selected_target_id,
        raw_dice_mod,
        raw_dice_adv_dis_stacks,
    } = sl_context;
    let target_id = selected_target_id;

    // Validate required fields
    const rules = {
        roll_private: { type: 'enum', value: roll_private, allowed: ['0', '1', '2'] },
        caster_id: { type: 'uuid', value: caster_id, optional: true },
        raw_dice_mod: { type: 'number', value: raw_dice_mod },
        raw_dice_adv_dis_stacks: { type: 'number', value: raw_dice_adv_dis_stacks },
        item_id_used: { type: 'uuid', value: item_id_used },
        // Second-slot choices (including chosen orbs) legitimately leave the first slot empty.
        use_ability_variable_1: { type: 'string', value: use_ability_variable_1, optional: true },
        use_ability_variable_2: { type: 'string', value: use_ability_variable_2 },
    };
    const validationResult = validateContext(rules, 'useAbilityVariable2');
    if (!validationResult.valid) {
        refuseUnported('ability-use:invalid-context', { error: validationResult.error });
        return;
    }

    // A SIZED CRAFT START (the PC sheet's size picker: variable_1 empty, variable_2 the SizeType rung) — the craft start lane (Wizard v9 WP6).
    if (!consume_inventory_id && typeof use_ability_variable_2 === 'string'
        && await craftStartLane({ casterId: String(caster_id ?? ''), encounterId: String(encounter_id ?? ''), recipeId: item_id_used,
            sizeId: use_ability_variable_2.trim() || null, rollPrivate: roll_private, variable1: use_ability_variable_1 })) return;

    if (clientTargetGateBlocksUse(item_id_used, caster_id, target_id, encounter_id)) return;
    // The cast-time TARGET gates (R-103) ahead of every client-truth lane; the unseen picks they drop leave this dispatch's selection.
    const targetGates = preLaneTargetGates({ item_id_used, caster_id, target_id, encounter_id, campaign_id, use_ability_variable_1: use_ability_variable_1 });
    if (targetGates) { const gated = await targetGates; if (gated.refused) return; target_id = gated.targetId as typeof target_id; }

    // The chosen-orb modal leaves slot 1 empty and sends its slug CSV in slot 2 (orbs/orb-cast.ts).
    if (typeof w.ftTryCommitAbilityUse === 'function' && encounter_id && caster_id && !consume_inventory_id
        && Object.keys(quick_ritual_fields).length === 0 && !window.aoeManager?.ft3dActive
        && (use_ability_variable_1 == null || use_ability_variable_1 === '')
        && typeof use_ability_variable_2 === 'string' && /^[a-z ,]{0,512}$/.test(use_ability_variable_2)) {
        const ability = abilityBlob(item_id_used);
        if (ability && ftLayerValue(ability, 'channel_chosen_orb') != null) {
            const parsedTargets = w.parseTargetIdField?.(String(target_id ?? '')) ?? { mapPointToken: null, entityIds: [] };
            let targetIds = Array.isArray(parsedTargets.entityIds) ? (parsedTargets.entityIds as unknown[]).map(v => String(v ?? '').trim()).filter(Boolean) : [];
            // A Self-range cast without an area resolves on the caster whatever was selected (validate_and_adjust_targets).
            if (!targetIds.length && !parsedTargets.mapPointToken && String(ftLayerValue(ability, 'range') ?? '').toUpperCase() === 'D82855E1-4BB9-93EA-6374-B9904318BC1D'
                && ftLayerValue(ability, 'aoe_type') == null) targetIds = [String(caster_id)];
            if (targetIds.length > 0 && !parsedTargets.mapPointToken) {
                const gathered = await ensureAbilityGateData({ casterId: String(caster_id), abilityId: item_id_used, encounterId: encounter_id });
                const gate = browserAbilityGateVerdict({ casterId: String(caster_id), encounterId: String(encounter_id), ability, gathered });
                if (gate.kind === 'refuse') { displayWarning(gate.message); cancelTargetDraft('ability-request-refused'); return; }
                if (gate.kind === 'allow') {
                    let outcome: 'handled' | 'refused' | null = null;
                    try {
                        outcome = await w.ftTryCommitAbilityUse({ encounterId: String(encounter_id), actorId: String(caster_id), targetIds,
                            abilityId: String(item_id_used), rawDiceMod: Number(raw_dice_mod) || 0, rawDiceAdvDisStacks: Number(raw_dice_adv_dis_stacks) || 0,
                            rollPrivate: Number(roll_private), orbPicks: use_ability_variable_2 });
                    } catch (error) { reportDeliveryThrow(error); console.error('ftTryCommitAbilityUse (orb picks) failed', error); displayWarning(escapeHtml(abilityDeliveryFailureMessage(error))); outcome = 'refused'; }
                    if (outcome !== null) { cancelActiveFt3dPlacement(); cancelTargetDraft('ability-request-complete'); return; }
                }
            }
        }
    }

    cancelActiveFt3dPlacement();

    // Refuse the unsupported variable contract.
    await queryAbilityData('legacy-ability-route-removed', {
        encounter_id, caster_id, item_id_used, use_ability_variable_1, use_ability_variable_2,
    });
    } finally { finishTimingInput?.(); }
}


// Sheet controls feed the same variable dispatchers as action-bar confirmation. These adapters
// only read DOM values; cast gates, placement, input families and delivery stay in those dispatchers.
async function useAbilityInput1(item_id_used: unknown, use_ability_input_1: unknown) {
    const value = (document.getElementById(String(use_ability_input_1 ?? '')) as HTMLInputElement | HTMLSelectElement | null)?.value || '';
    return value === '' ? useAbility(item_id_used) : useAbilityVariable1(item_id_used, value);
}

async function useAbilityInput1Input2(item_id_used: unknown, use_ability_input_1: unknown, use_ability_input_2: unknown) {
    const value1 = (document.getElementById(String(use_ability_input_1 ?? '')) as HTMLInputElement | HTMLSelectElement | null)?.value || '';
    const value2 = (document.getElementById(String(use_ability_input_2 ?? '')) as HTMLInputElement | HTMLSelectElement | null)?.value || '';
    if (value2 !== '') return useAbilityVariable2(item_id_used, value1, value2);
    return value1 === '' ? useAbility(item_id_used) : useAbilityVariable1(item_id_used, value1);
}

async function useAbilityVariable1Input1(item_id_used: unknown, use_ability_variable_1: unknown, use_ability_input_1: unknown) {
    const value = (document.getElementById(String(use_ability_input_1 ?? '')) as HTMLInputElement | HTMLSelectElement | null)?.value || '';
    return value === '' ? useAbilityVariable1(item_id_used, use_ability_variable_1)
        : useAbilityVariable2(item_id_used, use_ability_variable_1, value);
}

// WINDOW EXPORTS
Object.assign(w, {
    ftClientTargetGateBlocksUse: clientTargetGateBlocksUse,
    ftUseAbilityAtExplicitTarget,
    handleAbilityBtnSingleClick,
    handleuseAbilityVariable1BtnSingleClick,
    handleuseAbilityVariable2BtnSingleClick,
    handleuseAbilityInput1BtnSingleClick,
    handleuseAbilityInput1Input2BtnSingleClick,
    useAbility,
    useAbilityWithWeapon,
    useAbilityVariable1,
    useAbilityRitualSpellWithUserChoice1Summon,
    useAbilityVariable2,
    useAbilityInput1,
    useAbilityInput1Input2,
    useAbilityVariable1Input1,
});

export {};
